'use strict';

const libPictProvider = require('pict-provider');
// The app's segmented CodeMirror markdown editor (same component the Support composer uses). Subclassed below so the
// concierge's boxes never inline a dropped/pasted file as text or a base64 data: URI, and never preview an <img>.
const libMarkdownEditor = require('pict-section-markdowneditor');

const FLOWS = [ 'create', 'update', 'examples', 'build' ];

// Composer chrome per flow. Every string here is ours (safe to put in HTML and in the picker's pict templates).
const FLOW_META =
{
	create:   { Label: 'Create a form',     Icon: 'fa-file-circle-plus', Go: 'Create the form',   EditorLabel: 'Describe the form you need' },
	update:   { Label: 'Update a form',     Icon: 'fa-pen-to-square',    Go: 'Send the update',   EditorLabel: 'What should change?' },
	examples: { Label: 'Generate examples', Icon: 'fa-vials',            Go: 'Generate examples', EditorLabel: 'Anything specific about the examples? (optional)' },
	build:    { Label: 'Build & deliver',   Icon: 'fa-truck-ramp-box',   Go: 'Build & deliver',   EditorLabel: 'Notes for the build (optional)' },
};

// Session.Flow (the façade's label) → chip text. Unknown values are escaped and shown as-is.
const FLOW_CHIP = { create: 'Create', author: 'Create', update: 'Update', modify: 'Update', build: 'Build & deliver', config: 'Build & deliver', examples: 'Examples' };

// Run phase (see runPhase) → chip/status chrome.
const PHASE_META =
{
	answered:              { Chip: 'Answered',          Cls: 'fcg-st-ok',   Icon: 'fa-circle-check' },
	failed:                { Chip: 'Failed',            Cls: 'fcg-st-err',  Icon: 'fa-circle-exclamation' },
	cancelled:             { Chip: 'Cancelled',         Cls: 'fcg-st-off',  Icon: 'fa-ban' },
	'cancelled-unstarted': { Chip: 'Cancelled',         Cls: 'fcg-st-off',  Icon: 'fa-ban' },
	notpickedup:           { Chip: 'Not picked up',     Cls: 'fcg-st-warn', Icon: 'fa-plug-circle-exclamation' },
	cancelling:            { Chip: 'Cancelling',        Cls: 'fcg-st-warn', Icon: 'fa-hourglass-half' },
	stalled:               { Chip: 'Stalled?',          Cls: 'fcg-st-warn', Icon: 'fa-triangle-exclamation' },
	working:               { Chip: 'Working',           Cls: 'fcg-st-run',  Icon: 'fa-gears' },
	starting:              { Chip: 'Starting',          Cls: 'fcg-st-run',  Icon: 'fa-circle-notch' },
	waiting:               { Chip: 'Waiting',           Cls: 'fcg-st-run',  Icon: 'fa-hourglass-start' },
};

// Sessions-list Status (plansheet Request.Status, plus the façade's NotPickedUp) → chip chrome.
const SESSION_STATUS_META =
{
	Pending:     { Chip: 'Pending',       Cls: 'fcg-st-run' },
	Running:     { Chip: 'Running',       Cls: 'fcg-st-run' },
	Answered:    { Chip: 'Answered',      Cls: 'fcg-st-ok' },
	Failed:      { Chip: 'Failed',        Cls: 'fcg-st-err' },
	Cancelled:   { Chip: 'Cancelled',     Cls: 'fcg-st-off' },
	NotPickedUp: { Chip: 'Not picked up', Cls: 'fcg-st-warn' },
};

// Friendly text for the façade's error codes (§3 + amendment B/C). Everything else falls back to the error message.
const ERROR_TEXT =
{
	NodeBusy: 'The plansheet node is busy with another request. Try again when it finishes.',
	RunInProgress: 'This session still has a run in progress.',
	NotDelivered: 'This form has not been delivered (pushed) to Headlight yet. Build & deliver it first.',
	NoConfigSession: 'This form has no answered Build & deliver session yet, so there is nothing to seed examples from.',
	TextRequired: 'Describe the change you want first.',
	DeliveryTargetRequired: 'Say where to deliver the form, or tick Validate only.',
	NoBlueprint: 'This form has no agreed blueprint yet.',
	CorpusTooLarge: 'This form\'s sources are over the 1.5 MB text budget. Remove or trim some sources first.',
	ShapeUnavailable: 'Plansheet\'s prompt shape for this task could not be read, so nothing was started.',
	InputDropped: 'Plansheet dropped the request text, so the run was cancelled. Nothing else was changed.',
	PlansheetAuthFailed: 'Plansheet rejected the concierge\'s credentials (a server configuration problem).',
	PlansheetUnavailable: 'Plansheet is not answering right now. Try again in a minute.',
	PlansheetUserMismatch: 'This session belongs to a different plansheet user than this environment\'s token.',
	NotConfigured: 'Form Concierge is not configured on this server.',
	NotLoggedIn: 'Your Headlight session has expired. Sign in again.',
	NotAllowed: 'Form Concierge is for platform super-users only.',
	BadOrigin: 'The request was refused (cross-origin check).',
	AlreadyClaimed: 'That plansheet project is already claimed by Form Concierge (here or in another environment).',
	NotFound: 'Not found, or not one of this customer\'s Concierge forms.',
	NetworkError: 'The network request failed. Check your connection and try again.',
	Aborted: 'The upload was cancelled.',
};

// Error codes after which a session poll gives up (nothing a retry can fix).
const POLL_FATAL_CODES = [ 'NotFound', 'NotAllowed', 'NotLoggedIn', 'NotConfigured', 'PlansheetUserMismatch', 'BadOrigin' ];

const MB = 1024 * 1024;
const MAX_UPLOAD_BYTES = 25 * MB;         // façade upload cap (B6)
const REFERENCE_MAX_BYTES = 5 * MB;       // a binary with no extracted text is linked as a Reference only up to this (C8)
const MAX_CONCURRENT_UPLOADS = 2;         // D5 / B6
const MAX_SOURCES_PER_CALL = 25;          // façade Sources cap (B5)

const POLL_FAST_MS = 5000;
const POLL_MID_MS = 15000;
const POLL_SLOW_MS = 30000;
const POLL_FAST_UNTIL_MS = 2 * 60 * 1000;
const POLL_MID_UNTIL_MS = 10 * 60 * 1000;
const POLL_CAP_MS = 60 * 60 * 1000;       // D11: from the last explicit user action
const TICK_MS = 1000;

const STARTING_MS = 90 * 1000;            // unclaimed and younger than this → "Starting…"
const STALL_MS = 10 * 60 * 1000;          // claimed, no heartbeat for this long → "Stalled?"

const USER_WAIT_TRIES = 40;               // Support's CurrentUser wait: 150 ms × 40, then fail closed
const USER_WAIT_MS = 150;

const SESSION_LIST_SHOW = 30;
const PICKER_PAGE = 60;
const PICKER_HASH = 'FormConcierge-FormPicker';

const GROUP_INDEXED = 'Concierge forms';
const GROUP_UNINDEXED = 'Plansheet, not yet in Concierge';

// The two markdown boxes (D6): unique view ids, mounts and data addresses.
const EDITORS =
{
	composer: { ViewID: 'FormConcierge-Composer-Editor', Mount: '#FCG-Editor-Mount', Address: 'AppData.FormConcierge.ComposerSegments', Key: 'ComposerSegments', Draft: '_draft' },
	followup: { ViewID: 'FormConcierge-FollowUp-Editor', Mount: '#FCG-FU-Editor-Mount', Address: 'AppData.FormConcierge.FollowUpSegments', Key: 'FollowUpSegments', Draft: '_followDraft' },
};

// File regions per staging context.
const FILE_REGIONS = { composer: '#FCG-Files', followup: '#FCG-FU-Files' };

// Fallbacks for the shared steering constants (C1) when the Markdown module does not supply them.
const DEFAULT_EXAMPLES_PREFIX = '[examples] ';
const DEFAULT_STEERING_MARKER = '\n\n---\nForm Concierge steering:\n';
const DEFAULT_REVISION_PREFIX = '[revision] ';

/**
 * The sibling Form Concierge modules, loaded on first use (tests inject fakes through provider._deps instead).
 * @return {{ Api: any, Markdown: any, FileText: any, Mirror: any }}
 */
function loadDefaultDeps()
{
	return {
		Api: require('./FormConcierge-Api.js'),
		Markdown: require('./FormConcierge-Markdown.js'),
		FileText: require('./FormConcierge-FileText.js'),
		Mirror: require('./FormConcierge-Mirror.js'),
	};
}

/**
 * The concierge's markdown box: pict-section-markdowneditor with every file path (image paste, file drop, the image
 * picker) handed to the screen's drop zone, which uploads it like any other source file.
 */
class FormConciergeMarkdownEditor extends libMarkdownEditor
{
	/**
	 * A pasted or dropped image goes to the drop zone; the editor never inlines it as a data: URI.
	 * @param {File} pFile - The image file.
	 * @param {number} pSegmentIndex - Segment index (unused; there is one segment).
	 * @param {function} fCallback - Editor callback (never called: no image markdown is inserted).
	 * @returns {boolean} Always true (handled).
	 */
	onImageUpload(pFile, pSegmentIndex, fCallback)
	{
		const tmpSelf = /** @type {any} */ (this);
		const tmpHost = tmpSelf.pict && tmpSelf.pict.providers && tmpSelf.pict.providers.FormConcierge;
		if (pFile && tmpHost && (typeof tmpHost.addFiles === 'function'))
		{
			tmpHost.addFiles([ pFile ], tmpSelf.options.FCGContext);
		}
		return true;
	}

	/**
	 * Put a file-drop handler FIRST in the CodeMirror handler chain, so a dropped file (any type, any count) reaches
	 * the drop zone instead of CodeMirror's default read-as-text insert or the editor's own first-image-only handler.
	 * Text drags (no files) fall through to the normal behaviour.
	 * @param {Array} pExtensions - The extensions array to modify.
	 * @param {number} pSegmentIndex - The segment index.
	 * @returns {Array} The modified extensions array.
	 */
	customConfigureExtensions(pExtensions, pSegmentIndex)
	{
		const tmpSelf = /** @type {any} */ (this);
		const tmpCM = tmpSelf._codeMirrorModules;
		if (!Array.isArray(pExtensions) || !tmpCM || !tmpCM.EditorView || (typeof tmpCM.EditorView.domEventHandlers !== 'function'))
		{
			return pExtensions;
		}
		pExtensions.unshift(tmpCM.EditorView.domEventHandlers(
			{
				drop: (pEvent) =>
				{
					const tmpFiles = pEvent && pEvent.dataTransfer && pEvent.dataTransfer.files;
					if (!tmpFiles || !tmpFiles.length)
					{
						return false;
					}
					pEvent.preventDefault();
					// The editor's container-level listener would otherwise forward the first image a second time.
					pEvent.stopPropagation();
					const tmpHost = tmpSelf.pict && tmpSelf.pict.providers && tmpSelf.pict.providers.FormConcierge;
					if (tmpHost && (typeof tmpHost.addFiles === 'function'))
					{
						tmpHost.addFiles(tmpFiles, tmpSelf.options.FCGContext);
					}
					return true;
				},
			}));
		return pExtensions;
	}
}

/**
 * FormConciergeProvider — the #/FormConcierge screen (super-users only). Drop files, type one markdown request, press
 * Go, and hold a conversation with the plansheet form pipeline through the same-origin façade (/formconcierge/1.0/).
 * Flows: create (Author task on a new form project), update (Author task on an existing one), build (Config task:
 * build & deliver), examples (a follow-up on the form's delivered config session). Sessions are plansheet Requests,
 * mirrored into Headlight as FormConcierge-Session Documents.
 *
 * Painting is region-based (contract D7): the host skeleton is painted once per mode, and each region repaints only
 * itself — the mounted editor, picker and file list never get torn down by a status change. Every upstream string
 * (assistant AND user turns, names, metadata, guidance, errors) is untrusted: it goes through
 * FormConcierge-Markdown or _esc()/textContent, never a pict template. Inline handlers carry only Number()-coerced
 * ids or fixed literals; copy buttons resolve through an index into provider state.
 */
class FormConciergeProvider extends libPictProvider
{
	/**
	 * @param {any} pFable
	 * @param {any} pOptions
	 * @param {any} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		/** @type {any} */
		this._deps = null;
		this._initState();
	}

	/**
	 * Reset every piece of instance state (the constructor calls this; prototype-built test instances call it too).
	 */
	_initState()
	{
		this._mount = '#FormConcierge-Host';
		/** @type {any} */
		this._api = null;
		this._loadGen = 0;
		this._userWait = 0;
		/** @type {any} */
		this._pendingApply = null;
		/** @type {any} the last route applied (a Retry after a failed load re-applies it, not Home) */
		this._lastApply = null;
		this._locked = false;
		this._statusLoaded = false;
		/** @type {any} */
		this._status = null;
		/** @type {any} */
		this._nodeBusy = null;
		this._workspaceReady = false;
		/** @type {any} */
		this._forms = null;
		this._formsError = '';
		/** @type {any} */
		this._sessions = null;
		this._sessionsError = '';
		this._sessionsLoading = false;
		this._showAllSessions = false;
		/** @type {any} */
		this._mirror = null;
		/** @type {any} */
		this._mirrorReady = null;
		/** @type {any} */
		this._mirrorReadyPromise = null;
		/** @type {Record<string, any>} */
		this._mirrorState = {};
		/** @type {Record<string, string>} */
		this._mirroredSig = {};
		/** @type {Record<string, Array<any>>} */
		this._sessionFiles = {};
		this._paintedMode = '';
		this._draft = '';
		this._followDraft = '';
		this._followFor = 0;
		this._formName = '';
		this._deliverTo = '';
		this._validateOnly = false;
		/** @type {{ composer: Array<any>, followup: Array<any> }} */
		this._stage = { composer: [], followup: [] };
		this._fileSeq = 0;
		/** @type {any} */
		this._session = null;
		this._threadSig = '';
		this._runKey = '';
		this._headKey = '';
		/** @type {any} */
		this._phase = null;
		/** @type {Record<string, boolean>} */
		this._openDetails = {};
		/** @type {Array<string>} */
		this._copyables = [];
		this._pollGen = 0;
		/** @type {any} */
		this._pollTimer = null;
		/** @type {any} */
		this._tickTimer = null;
		this._pollToken = -1;
		this._pollCapStart = 0;
		this._pollCapped = false;
		this._pollError = '';
		/** @type {any} */
		this._visHandler = null;
		this._skew = 0;
		/** @type {any} */
		this._hostListenerEl = null;
	}

	// ── Collaborators ──────────────────────────────────────────────────────────────────────────────────────────

	/** @return {any} the injected (tests) or real sibling modules */
	get deps()
	{
		if (!this._deps)
		{
			this._deps = loadDefaultDeps();
		}
		return this._deps;
	}

	/** @return {any} the façade client (one per provider) */
	get api()
	{
		if (!this._api)
		{
			const tmpApiModule = this.deps.Api;
			const tmpClass = (tmpApiModule && tmpApiModule.FormConciergeApi) || tmpApiModule;
			this._api = new tmpClass({});
		}
		return this._api;
	}

	/** @return {any} the strict remote-markdown module */
	get md() { return this.deps.Markdown || {}; }

	/** @return {any} */
	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }

	/** @return {any} */
	get modal() { return this.pict.views && this.pict.views['Pict-Section-Modal']; }

	/** @return {any} */
	get pickerProvider() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }

	/** @return {number} the session customer */
	get idCustomer() { return Number((this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0); }

	/** @return {any} the screen state (AppData.FormConcierge; also holds the editors' segment arrays) */
	get state()
	{
		if (!this.pict.AppData.FormConcierge)
		{
			this.pict.AppData.FormConcierge =
			{
				Mode: 'home', Flow: 'create', IDProject: 0, IDRequest: 0,
				Loading: false, Error: '', Busy: false, BusyText: '', SendBusy: false,
				ComposerSegments: [ { Content: '' } ], FollowUpSegments: [ { Content: '' } ], PickedForm: '',
			};
		}
		return this.pict.AppData.FormConcierge;
	}

	// ── Small helpers ──────────────────────────────────────────────────────────────────────────────────────────

	/** @return {number} wall-clock ms (injectable for tests) */
	_now()
	{
		const tmpDeps = this._deps || {};
		return (typeof tmpDeps.now === 'function') ? tmpDeps.now() : Date.now();
	}

	/**
	 * setTimeout through the injectable clock.
	 * @param {Function} fCallback
	 * @param {number} pMs
	 * @return {any} handle
	 */
	_setTimer(fCallback, pMs)
	{
		const tmpDeps = this._deps || {};
		return (typeof tmpDeps.setTimeout === 'function') ? tmpDeps.setTimeout(fCallback, pMs) : setTimeout(fCallback, pMs);
	}

	/**
	 * clearTimeout through the injectable clock.
	 * @param {any} pHandle
	 */
	_clearTimer(pHandle)
	{
		if (!pHandle)
		{
			return;
		}
		const tmpDeps = this._deps || {};
		if (typeof tmpDeps.clearTimeout === 'function')
		{
			tmpDeps.clearTimeout(pHandle);
		}
		else
		{
			clearTimeout(pHandle);
		}
	}

	/** @return {number} the server's clock, estimated from the last GET Session ServerNow */
	_serverNow() { return this._now() + (this._skew || 0); }

	/** @return {any} document, or null outside a browser */
	_doc() { return (typeof document !== 'undefined') ? document : null; }

	/** @return {any} window, or null outside a browser */
	_win() { return (typeof window !== 'undefined') ? window : null; }

	/**
	 * @param {string} pID - element id (no '#')
	 * @return {any} the element or null
	 */
	_el(pID)
	{
		const tmpDoc = this._doc();
		return tmpDoc ? tmpDoc.getElementById(pID) : null;
	}

	/**
	 * @param {string} pSelector
	 * @return {boolean} whether the selector is in the DOM
	 */
	_has(pSelector)
	{
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || (typeof tmpCA.getElement !== 'function'))
		{
			return false;
		}
		try
		{
			const tmpSet = tmpCA.getElement(pSelector);
			return !!(tmpSet && tmpSet.length > 0);
		}
		catch (pError)
		{
			return false;
		}
	}

	/**
	 * Replace one region's HTML (no-op when the region is not painted).
	 * @param {string} pSelector
	 * @param {string} pHTML
	 * @return {boolean} painted
	 */
	_assign(pSelector, pHTML)
	{
		if (!this._has(pSelector))
		{
			return false;
		}
		this.pict.ContentAssignment.assignContent(pSelector, pHTML);
		return true;
	}

	/**
	 * HTML-escape (the Markdown module's escapeHTML when present; same entity set otherwise).
	 * @param {any} pValue
	 * @return {string}
	 */
	_esc(pValue)
	{
		const tmpMd = this._deps && this._deps.Markdown;
		if (tmpMd && (typeof tmpMd.escapeHTML === 'function'))
		{
			return String(tmpMd.escapeHTML(pValue == null ? '' : pValue));
		}
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[pChar]));
	}

	/**
	 * Untrusted markdown → safe HTML (strict renderer; escaped <pre> if it throws).
	 * @param {any} pText
	 * @return {string}
	 */
	_md(pText)
	{
		const tmpText = String(pText == null ? '' : pText);
		try
		{
			return String(this.md.renderRemoteMarkdown(tmpText));
		}
		catch (pError)
		{
			return `<pre class="fcg-pre">${this._esc(tmpText)}</pre>`;
		}
	}

	/**
	 * Strip the façade's steering (C6) from a turn's text.
	 * @param {any} pText
	 * @return {string}
	 */
	_stripSteering(pText)
	{
		const tmpText = String(pText == null ? '' : pText);
		if (typeof this.md.stripSteering === 'function')
		{
			try
			{
				return String(this.md.stripSteering(tmpText));
			}
			catch (pError)
			{
				// fall through to the local rule
			}
		}
		let tmpOut = tmpText;
		const tmpMarker = tmpOut.indexOf(this.md.STEERING_MARKER || DEFAULT_STEERING_MARKER);
		if (tmpMarker >= 0)
		{
			tmpOut = tmpOut.slice(0, tmpMarker);
		}
		const tmpPrefixes = [ this.md.EXAMPLES_PREFIX || DEFAULT_EXAMPLES_PREFIX, this.md.REVISION_PREFIX || DEFAULT_REVISION_PREFIX ];
		if (tmpPrefixes.some((pPrefix) => tmpOut.indexOf(pPrefix) === 0))
		{
			const tmpBreak = tmpOut.indexOf('\n\n');
			tmpOut = (tmpBreak >= 0) ? tmpOut.slice(tmpBreak + 2) : '';
		}
		return tmpOut;
	}

	/**
	 * A strict positive id (^[1-9]\d{0,9}$) → Number, anything else → 0.
	 * @param {any} pValue
	 * @return {number}
	 */
	_validID(pValue)
	{
		const tmpString = String(pValue == null ? '' : pValue).trim();
		return /^[1-9]\d{0,9}$/.test(tmpString) ? Number(tmpString) : 0;
	}

	/**
	 * A name as the façade stores it: control chars and slashes stripped, trimmed, cut (B5).
	 * @param {any} pName
	 * @param {number} pMax
	 * @return {string}
	 */
	_cleanName(pName, pMax)
	{
		return String(pName == null ? '' : pName).replace(/[\x00-\x1f\x7f/\\]/g, '').trim().slice(0, pMax);
	}

	/**
	 * @param {any} pValue
	 * @return {number} ms since epoch, or 0
	 */
	_ms(pValue)
	{
		if (!pValue)
		{
			return 0;
		}
		const tmpMs = Date.parse(String(pValue));
		return isNaN(tmpMs) ? 0 : tmpMs;
	}

	/**
	 * @param {number} pMs
	 * @return {string} m:ss or h:mm:ss
	 */
	_fmtElapsed(pMs)
	{
		const tmpSeconds = Math.max(0, Math.floor((Number(pMs) || 0) / 1000));
		const tmpH = Math.floor(tmpSeconds / 3600);
		const tmpM = Math.floor((tmpSeconds % 3600) / 60);
		const tmpS = tmpSeconds % 60;
		const fPad = (pN) => ((pN < 10) ? '0' : '') + pN;
		return tmpH ? `${tmpH}:${fPad(tmpM)}:${fPad(tmpS)}` : `${tmpM}:${fPad(tmpS)}`;
	}

	/**
	 * @param {any} pValue - ISO date
	 * @return {string} "5 min ago" style, or ''
	 */
	_relTime(pValue)
	{
		const tmpMs = this._ms(pValue);
		if (!tmpMs)
		{
			return '';
		}
		const tmpAgo = this._serverNow() - tmpMs;
		if (tmpAgo < 60000)
		{
			return 'just now';
		}
		if (tmpAgo < 3600000)
		{
			return `${Math.round(tmpAgo / 60000)} min ago`;
		}
		if (tmpAgo < 86400000)
		{
			return `${Math.round(tmpAgo / 3600000)} h ago`;
		}
		if (tmpAgo < 7 * 86400000)
		{
			return `${Math.round(tmpAgo / 86400000)} d ago`;
		}
		return new Date(tmpMs).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}

	/**
	 * @param {any} pValue - ISO date
	 * @return {string} short local date + time, or ''
	 */
	_dateTime(pValue)
	{
		const tmpMs = this._ms(pValue);
		return tmpMs ? new Date(tmpMs).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
	}

	/**
	 * @param {number} pBytes
	 * @return {string}
	 */
	_fmtSize(pBytes)
	{
		const tmpBytes = Number(pBytes) || 0;
		if (tmpBytes >= MB)
		{
			return `${(tmpBytes / MB).toFixed(1)} MB`;
		}
		if (tmpBytes >= 1024)
		{
			return `${Math.round(tmpBytes / 1024)} KB`;
		}
		return `${tmpBytes} B`;
	}

	/**
	 * Toast through pict-section-modal (escapes the message; duration only when given, never undefined).
	 * @param {string} pMessage
	 * @param {string} [pType]
	 * @param {number} [pDuration]
	 */
	_toast(pMessage, pType, pDuration)
	{
		const tmpModal = this.modal;
		if (!tmpModal || (typeof tmpModal.toast !== 'function'))
		{
			return;
		}
		/** @type {any} */
		const tmpOptions = { type: pType || 'info' };
		if (Number(pDuration) > 0)
		{
			tmpOptions.duration = Number(pDuration);
		}
		else if ((pType === 'error') || (pType === 'warning'))
		{
			tmpOptions.duration = 7000;
		}
		tmpModal.toast(String(pMessage == null ? '' : pMessage), tmpOptions);
	}

	/**
	 * A user-facing sentence for a thrown error (façade codes mapped; everything shown is escaped by the caller).
	 * @param {any} pError
	 * @return {string}
	 */
	_errorText(pError)
	{
		const tmpCode = pError && (typeof pError.Code === 'string') ? pError.Code : '';
		if (tmpCode && ERROR_TEXT[tmpCode])
		{
			return ERROR_TEXT[tmpCode];
		}
		if ((tmpCode === 'BadRequest') && pError.Detail)
		{
			return `The request was rejected: ${String(pError.Detail).slice(0, 200)}`;
		}
		const tmpMessage = (pError && pError.message) ? pError.message : String(pError || 'Something went wrong.');
		return String(tmpMessage).slice(0, 300);
	}

	/** @return {number} the router's navigation token (latest-wins) */
	_routeToken()
	{
		const tmpRouter = this.pict.providers && this.pict.providers.PlatformConfigurationRouter;
		return tmpRouter ? (Number(tmpRouter._routeToken) || 0) : 0;
	}

	/**
	 * Write the URL hash (the router applies it). Tests replace this method.
	 * @param {string} pHash - e.g. '#/FormConcierge/Session/12'
	 */
	_navigate(pHash)
	{
		const tmpWindow = this._win();
		if (tmpWindow && tmpWindow.location)
		{
			tmpWindow.location.hash = pHash;
		}
	}

	/** @return {any} the app's navigo instance (pict-router keeps it at PictRouter.router), or null */
	_navigo()
	{
		const tmpPictRouter = this.pict.providers && this.pict.providers.PictRouter;
		const tmpNavigo = tmpPictRouter && tmpPictRouter.router;
		return (tmpNavigo && (typeof tmpNavigo.navigate === 'function')) ? tmpNavigo : null;
	}

	/**
	 * Rewrite the URL without routing (D9: a created form's id survives a reload).
	 *
	 * navigo is told about the new route too, without running its handler or hooks: a bare history.replaceState
	 * leaves navigo's lastResolved on the old route, so a later link to that old route resolves as "already
	 * there" and does nothing. navigo writes no URL itself here (updateBrowserURL:false): its own write freezes
	 * its popstate listener for ~1 ms, which can swallow the popstate of the _navigate() that Go fires right after.
	 * @param {string} pHash - e.g. '#/FormConcierge/create/77'
	 */
	_replaceHash(pHash)
	{
		const tmpHash = String(pHash || '');
		const tmpNavigo = this._navigo();
		if (tmpNavigo)
		{
			try
			{
				tmpNavigo.navigate(tmpHash.replace(/^#?\/?/, ''), { callHandler: false, callHooks: false, updateBrowserURL: false });
			}
			catch (pError)
			{
				// best-effort: the URL write below still happens
			}
		}
		const tmpWindow = this._win();
		try
		{
			if (tmpWindow && tmpWindow.history && (typeof tmpWindow.history.replaceState === 'function'))
			{
				tmpWindow.history.replaceState(null, '', tmpHash);
			}
		}
		catch (pError)
		{
			// best-effort: the id is still kept in state
		}
	}

	// ── Gate ───────────────────────────────────────────────────────────────────────────────────────────────────

	/** @return {boolean} platform super-user (customer 1, role 5); fail-closed */
	_isSuperUser()
	{
		const tmpApp = this.pict.PictApplication;
		if (tmpApp && (typeof tmpApp._isSuperUser === 'function'))
		{
			try
			{
				return !!tmpApp._isSuperUser();
			}
			catch (pError)
			{
				return false;
			}
		}
		const tmpUser = this.pict.AppData && this.pict.AppData.CurrentUser;
		return !!(tmpUser && (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1));
	}

	/** @return {boolean} CurrentUser has arrived */
	_currentUserReady()
	{
		const tmpUser = this.pict.AppData && this.pict.AppData.CurrentUser;
		return !!(tmpUser && (tmpUser.UserID || tmpUser.LoginID || (tmpUser.UserRoleIndex !== undefined)));
	}

	/** @return {boolean} the façade answered Configured + Allowed */
	_isReady()
	{
		const tmpStatus = this._status;
		return !!(tmpStatus && tmpStatus.Configured && tmpStatus.Allowed && !tmpStatus.Unreachable);
	}

	/** @return {boolean} the screen can paint a working surface (not locked / loading / not configured) */
	_canRender() { return !this._locked && this._statusLoaded && !this.state.Loading && this._isReady(); }

	/**
	 * The plansheet task a flow needs (create/update → Author; build/examples → Config), or null.
	 * @param {string} pFlow
	 * @return {any}
	 */
	_taskFor(pFlow)
	{
		const tmpTasks = (this._status && this._status.Tasks) || {};
		return ((pFlow === 'build') || (pFlow === 'examples')) ? (tmpTasks.Config || null) : (tmpTasks.Author || null);
	}

	// ── Load ───────────────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Screen entry (the view calls this on every mount): wait for the user (fail closed), GET Status, then Forms +
	 * Sessions once, check the mirror once, and apply the pending route.
	 * @return {Promise<void>}
	 */
	async loadAsync()
	{
		const tmpGen = ++this._loadGen;
		const tmpState = this.state;
		// The editors write every keystroke into the segment arrays; take the drafts from there before anything
		// paints (a mount re-seeds the box from the draft), so text typed before leaving the screen comes back.
		this._refreshDrafts();
		this._stopPolling();
		this._paintedMode = '';
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Busy = false;
		tmpState.SendBusy = false;
		if (!this._currentUserReady())
		{
			this._paintNotice('loading');
			this._userWait = (this._userWait || 0) + 1;
			if (this._userWait <= USER_WAIT_TRIES)
			{
				this._setTimer(() =>
				{
					if (tmpGen === this._loadGen)
					{
						this.loadAsync();
					}
				}, USER_WAIT_MS);
				return;
			}
		}
		this._userWait = 0;
		if (!this._isSuperUser())
		{
			this._locked = true;
			this._statusLoaded = false;
			tmpState.Loading = false;
			this._pendingApply = null;
			this._paintNotice('locked');
			return;
		}
		this._locked = false;
		this._paintNotice('loading');
		try
		{
			const tmpStatus = await this.api.getStatus();
			if (tmpGen !== this._loadGen)
			{
				return;
			}
			this._status = tmpStatus || { Configured: false, Allowed: false, Unreachable: true };
			this._nodeBusy = this._status.NodeBusy || null;
			const tmpWorkspace = this._status.Workspace || {};
			this._workspaceReady = !!(tmpWorkspace.WorkflowData && tmpWorkspace.Forms);
			this._statusLoaded = true;
			if (this._isReady())
			{
				this._initMirror();
				await Promise.all([ this._loadFormsAsync(), this._loadSessionsAsync() ]);
				if (tmpGen !== this._loadGen)
				{
					return;
				}
				this._reconcileMirror();
			}
		}
		catch (pError)
		{
			if (tmpGen !== this._loadGen)
			{
				return;
			}
			this._statusLoaded = false;
			tmpState.Error = this._errorText(pError);
		}
		finally
		{
			if (tmpGen === this._loadGen)
			{
				tmpState.Loading = false;
				this._paintedMode = '';
				// A Retry (no pending apply) re-applies the last route, so a deep link survives a failed first load.
				const tmpApply = this._pendingApply || this._lastApply || ((pProvider) => pProvider.routeApplyHome());
				this._pendingApply = null;
				this._lastApply = tmpApply;
				try
				{
					tmpApply(this);
				}
				catch (pError)
				{
					if (this.pict.log && (typeof this.pict.log.error === 'function'))
					{
						this.pict.log.error(`FormConcierge route apply failed: ${pError && pError.message}`);
					}
					this._render();
				}
			}
		}
	}

	/**
	 * GET Forms into this._forms ({Forms, Unindexed}); an error is kept for the picker's empty label.
	 * @return {Promise<void>}
	 */
	async _loadFormsAsync()
	{
		try
		{
			const tmpResult = await this.api.getForms();
			this._forms =
			{
				Forms: Array.isArray(tmpResult && tmpResult.Forms) ? tmpResult.Forms : [],
				Unindexed: Array.isArray(tmpResult && tmpResult.Unindexed) ? tmpResult.Unindexed : [],
			};
			this._formsError = '';
		}
		catch (pError)
		{
			this._forms = this._forms || { Forms: [], Unindexed: [] };
			this._formsError = this._errorText(pError);
		}
	}

	/**
	 * GET Sessions into this._sessions.
	 * @return {Promise<void>}
	 */
	async _loadSessionsAsync()
	{
		this._sessionsLoading = true;
		try
		{
			const tmpResult = await this.api.getSessions();
			this._sessions = Array.isArray(tmpResult && tmpResult.Sessions) ? tmpResult.Sessions : [];
			this._sessionsError = '';
		}
		catch (pError)
		{
			this._sessions = this._sessions || [];
			this._sessionsError = this._errorText(pError);
		}
		this._sessionsLoading = false;
	}

	/**
	 * A Home load after a session visit: refetch Forms + Sessions (never polled), repaint their regions, reconcile.
	 * @return {Promise<void>}
	 */
	async _refreshHomeDataAsync()
	{
		this._sessionsLoading = true;
		this._paintSessions();
		await Promise.all([ this._loadFormsAsync(), this._loadSessionsAsync() ]);
		if (this.state.Mode !== 'home')
		{
			return;
		}
		this._paintSessions();
		this._paintTargetInfo();
		this._setPickerValue(this.state.IDProject);
		this._paintGo();
		this._reconcileMirror();
	}

	// ── Mirror (D12) ───────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Build the Headlight mirror and check its form type once for this screen entry.
	 */
	_initMirror()
	{
		this._mirror = null;
		this._mirrorReady = null;
		this._mirrorReadyPromise = null;
		try
		{
			const tmpModule = this.deps.Mirror;
			const tmpClass = (tmpModule && tmpModule.FormConciergeMirror) || tmpModule;
			if ((typeof tmpClass !== 'function') || !this.restClient)
			{
				this._mirrorReady = { Ready: false, Reason: 'The Headlight mirror is not available in this build.' };
				return;
			}
			this._mirror = new tmpClass({ rest: this.restClient, IDCustomer: this.idCustomer, Environment: (this._status && this._status.Environment) || '', log: this.pict.log });
			this._mirrorReadyPromise = Promise.resolve(this._mirror.checkReady()).then(
				(pReady) =>
				{
					this._mirrorReady = pReady || { Ready: false };
					this._paintSessionHeader(true);
					return this._mirrorReady;
				},
				(pError) =>
				{
					this._mirrorReady = { Ready: false, Reason: this._errorText(pError) };
					this._paintSessionHeader(true);
					return this._mirrorReady;
				});
		}
		catch (pError)
		{
			this._mirror = null;
			this._mirrorReady = { Ready: false, Reason: this._errorText(pError) };
		}
	}

	/**
	 * @param {any} pPayload - GET Session payload
	 * @return {string} the mirror signature (the Mirror module's when it has one)
	 */
	_mirrorSignature(pPayload)
	{
		const tmpModule = this.deps.Mirror;
		const tmpClass = (tmpModule && tmpModule.FormConciergeMirror) || tmpModule;
		if (tmpClass && (typeof tmpClass.signature === 'function'))
		{
			try
			{
				return String(tmpClass.signature(pPayload));
			}
			catch (pError)
			{
				// fall back to the thread signature
			}
		}
		return this._threadSignature(pPayload);
	}

	/**
	 * Fire-and-forget mirror sync when this session's signature changed since its last sync.
	 * @param {any} pPayload
	 */
	_maybeMirror(pPayload)
	{
		if (!this._mirror || this._pollCapped || !pPayload || !pPayload.Session)
		{
			return;
		}
		const tmpID = Number(pPayload.Session.IDRequest) || 0;
		if (!tmpID)
		{
			return;
		}
		const tmpSig = this._mirrorSignature(pPayload);
		if (this._mirroredSig[tmpID] === tmpSig)
		{
			return;
		}
		this._mirroredSig[tmpID] = tmpSig;
		const tmpMirror = this._mirror;
		Promise.resolve(this._mirrorReadyPromise)
			.then((pReady) =>
			{
				if (!pReady || !pReady.Ready)
				{
					return null;
				}
				const tmpFiles = this._sessionFiles[tmpID];
				return tmpMirror.syncSession(pPayload, tmpFiles ? { Files: tmpFiles } : {});
			})
			.then((pResult) =>
			{
				if (pResult)
				{
					this._mirrorState[tmpID] = pResult;
					this._paintSessionHeader(true);
				}
			})
			.catch((pError) =>
			{
				this._mirrorState[tmpID] = { Skipped: true, Reason: this._errorText(pError) };
				this._paintSessionHeader(true);
			});
	}

	/**
	 * Reconcile the mirror against the loaded Sessions list (fire-and-forget; the Mirror module bounds the work).
	 */
	_reconcileMirror()
	{
		const tmpMirror = this._mirror;
		const tmpSessions = this._sessions;
		if (!tmpMirror || !Array.isArray(tmpSessions) || !tmpSessions.length || (typeof tmpMirror.reconcile !== 'function'))
		{
			return;
		}
		Promise.resolve(this._mirrorReadyPromise)
			.then((pReady) =>
			{
				if (!pReady || !pReady.Ready)
				{
					return null;
				}
				return tmpMirror.reconcile(tmpSessions, (pIDRequest) => this.api.getSession(pIDRequest));
			})
			.catch(() =>
			{
				// best-effort; the next Home load tries again
			});
	}

	/**
	 * "Open in Headlight" for a mirrored session (index lookup by IDRequest; the handler passes only a Number).
	 * @param {number} pIDRequest
	 */
	openMirrorDocument(pIDRequest)
	{
		const tmpEntry = this._mirrorState[Number(pIDRequest) || 0];
		const tmpIDDocument = Number(tmpEntry && tmpEntry.IDDocument) || 0;
		const tmpRender = this.pict.HeadlightDocumentRender;
		if (!tmpIDDocument || !tmpRender || (typeof tmpRender.openDocument !== 'function'))
		{
			this._toast('The Headlight copy of this session is not available yet.', 'warning');
			return;
		}
		tmpRender.openDocument(tmpIDDocument, { target: '_blank', variation: 'html' }, (pError) =>
		{
			if (!pError)
			{
				return;
			}
			const tmpMessage = String((pError && pError.message) || pError);
			if (this.pict.log && (typeof this.pict.log.error === 'function'))
			{
				this.pict.log.error(`FormConcierge: opening mirror document ${tmpIDDocument} failed: ${tmpMessage}`);
			}
			this._toast(/blocked/i.test(tmpMessage)
				? 'Your browser blocked the new tab — allow pop-ups for this site and try again.'
				: `The Headlight copy of this session could not be opened: ${tmpMessage.slice(0, 200)}`, 'error');
		});
	}

	// ── Routes (D8) ────────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Router entry: #/FormConcierge → a fresh Create composer.
	 */
	routeApplyHome()
	{
		this.routeApplyFlow('create', 0);
	}

	/**
	 * Router entry: #/FormConcierge/:Flow[/:IDProject]. Same flow + new form → picker.setValue only; flow change →
	 * repaint Target + Guidance (draft and staged files carry over); entering Home → paint the home skeleton.
	 * @param {string} pFlow - create | update | examples | build
	 * @param {number} [pIDProject]
	 */
	routeApplyFlow(pFlow, pIDProject)
	{
		const tmpLower = String(pFlow == null ? '' : pFlow).toLowerCase();
		const tmpFlow = (FLOWS.indexOf(tmpLower) >= 0) ? tmpLower : 'create';
		const tmpID = this._validID(pIDProject);
		this._lastApply = (pProvider) => pProvider.routeApplyFlow(tmpFlow, tmpID);
		const tmpState = this.state;
		const tmpPrevMode = this._paintedMode;
		const tmpPrevFlow = tmpState.Flow;
		const tmpPrevID = Number(tmpState.IDProject) || 0;
		if (tmpState.Mode === 'session')
		{
			this._captureEditor('followup');
		}
		this._stopPolling();
		tmpState.Mode = 'home';
		tmpState.Flow = tmpFlow;
		tmpState.IDProject = tmpID;
		tmpState.IDRequest = 0;
		if (!this._canRender())
		{
			this._render();
			return;
		}
		if ((tmpPrevMode !== 'home') || !this._has('#FCG-Composer'))
		{
			this._paintHome();
			if (tmpPrevMode === 'session')
			{
				this._refreshHomeDataAsync();
			}
			return;
		}
		if (tmpPrevFlow !== tmpFlow)
		{
			this._captureEditor('composer');
			this._paintFlowTabs();
			this._paintTarget();
			this._paintGuidance();
			this._paintDropVisibility();
			this._paintGo();
			return;
		}
		if (tmpPrevID !== tmpID)
		{
			if (this._has('#FCG-FormPicker'))
			{
				this._setPickerValue(tmpID);
				this._paintTargetInfo();
			}
			else
			{
				// create (name input ↔ resumed form) and a fixed build target hold no widget: safe to repaint
				this._paintTarget();
			}
			this._paintGo();
		}
	}

	/**
	 * Router entry: #/FormConcierge/Session/:IDRequest — paint the session view, fetch, and poll (an explicit open).
	 * @param {number} pIDRequest
	 * @return {Promise<void>|void}
	 */
	routeApplySession(pIDRequest)
	{
		const tmpID = this._validID(pIDRequest);
		const tmpState = this.state;
		if (!tmpID)
		{
			this._navigate('#/FormConcierge');
			return;
		}
		this._lastApply = (pProvider) => pProvider.routeApplySession(tmpID);
		if (tmpState.Mode === 'home')
		{
			this._captureEditor('composer');
		}
		const tmpSame = (tmpState.Mode === 'session') && (Number(tmpState.IDRequest) === tmpID) && (this._paintedMode === 'session') && this._has('#FCG-Thread');
		if (Number(tmpState.IDRequest) !== tmpID)
		{
			this._session = null;
			this._threadSig = '';
			this._runKey = '';
			this._headKey = '';
			this._phase = null;
			this._openDetails = {};
			this._copyables = [];
		}
		// A follow-up draft (and its files) belongs to one session: keep it across a Home round-trip, drop it on a switch.
		if (this._followFor !== tmpID)
		{
			this._followFor = tmpID;
			this._followDraft = '';
			tmpState.FollowUpSegments = [ { Content: '' } ];
			this._clearStage('followup');
		}
		tmpState.Mode = 'session';
		tmpState.IDRequest = tmpID;
		if (!this._canRender())
		{
			this._render();
			return;
		}
		if (!tmpSame)
		{
			this._paintSession();
		}
		return this._openSession(true);
	}

	// ── Polling (D11) ──────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Fetch the open session now and keep polling. An explicit open restarts the 60-minute cap.
	 * @param {boolean} pExplicit - a user action (Go, Send, Cancel, Refresh, open by click/deep link)
	 * @return {Promise<void>}
	 */
	_openSession(pExplicit)
	{
		const tmpGen = this._beginPoll(pExplicit);
		return this._pollTick(tmpGen);
	}

	/**
	 * Start a new poll generation (kills any older chain) bound to the current route token.
	 * @param {boolean} pExplicit
	 * @return {number} the generation
	 */
	_beginPoll(pExplicit)
	{
		this._stopTimers();
		this._pollGen = (this._pollGen || 0) + 1;
		this._pollToken = this._routeToken();
		if (pExplicit || !this._pollCapStart)
		{
			this._pollCapStart = this._now();
		}
		this._pollCapped = false;
		this._pollError = '';
		return this._pollGen;
	}

	/** Stop every poll chain (bumps the generation). */
	_stopPolling()
	{
		this._pollGen = (this._pollGen || 0) + 1;
		this._stopTimers();
	}

	/** Clear the poll + ticker timers and the visibility wait. */
	_stopTimers()
	{
		this._clearTimer(this._pollTimer);
		this._pollTimer = null;
		this._clearTimer(this._tickTimer);
		this._tickTimer = null;
		const tmpDoc = this._doc();
		if (this._visHandler && tmpDoc)
		{
			tmpDoc.removeEventListener('visibilitychange', this._visHandler);
		}
		this._visHandler = null;
	}

	/**
	 * @param {number} pGen
	 * @return {boolean} this chain may still run (same generation, same route, thread still mounted)
	 */
	_pollAlive(pGen)
	{
		return (pGen === this._pollGen)
			&& (this._routeToken() === this._pollToken)
			&& (this.state.Mode === 'session')
			&& this._has('#FCG-Thread');
	}

	/** @return {boolean} the cap (60 min since the last explicit action) has passed */
	_capReached() { return (this._now() - (this._pollCapStart || 0)) >= POLL_CAP_MS; }

	/**
	 * One poll: guards, cap, hidden-tab wait, GET Session, apply, then schedule the next (unless terminal).
	 * @param {number} pGen
	 * @return {Promise<void>}
	 */
	async _pollTick(pGen)
	{
		if (!this._pollAlive(pGen))
		{
			return;
		}
		if (this._capReached())
		{
			this._hitCap();
			return;
		}
		const tmpDoc = this._doc();
		if (tmpDoc && tmpDoc.hidden)
		{
			this._waitVisible(pGen);
			return;
		}
		const tmpID = Number(this.state.IDRequest) || 0;
		let tmpPayload = null;
		try
		{
			tmpPayload = await this.api.getSession(tmpID);
		}
		catch (pError)
		{
			if (!this._pollAlive(pGen) || (Number(this.state.IDRequest) !== tmpID))
			{
				return;
			}
			this._pollError = this._errorText(pError);
			this._paintRunStatus(true);
			if (!this._session)
			{
				this._assign('#FCG-Thread', `<div class="fcg-note fcg-note-error"><i class="fas fa-circle-exclamation"></i> ${this._esc(this._pollError)}</div>`);
			}
			if (POLL_FATAL_CODES.indexOf(pError && pError.Code) >= 0)
			{
				this._stopTimers();
				return;
			}
			this._scheduleNext(pGen);
			return;
		}
		if (!this._pollAlive(pGen) || (Number(this.state.IDRequest) !== tmpID))
		{
			return;
		}
		this._pollError = '';
		try
		{
			this._applySessionPayload(tmpPayload);
		}
		catch (pError)
		{
			if (this.pict.log && (typeof this.pict.log.error === 'function'))
			{
				this.pict.log.error(`FormConcierge could not paint session ${tmpID}: ${pError && pError.message}`);
			}
		}
		if (this._phase && this._phase.Terminal)
		{
			this._stopTimers();
			return;
		}
		this._startTicker(pGen);
		this._scheduleNext(pGen);
	}

	/**
	 * Schedule the next poll on the 5 s → 15 s → 30 s ladder (measured from the last explicit action).
	 * @param {number} pGen
	 */
	_scheduleNext(pGen)
	{
		if (!this._pollAlive(pGen))
		{
			return;
		}
		const tmpElapsed = this._now() - (this._pollCapStart || 0);
		if (tmpElapsed >= POLL_CAP_MS)
		{
			this._hitCap();
			return;
		}
		let tmpDelay = POLL_SLOW_MS;
		if (tmpElapsed < POLL_FAST_UNTIL_MS)
		{
			tmpDelay = POLL_FAST_MS;
		}
		else if (tmpElapsed < POLL_MID_UNTIL_MS)
		{
			tmpDelay = POLL_MID_MS;
		}
		tmpDelay = Math.max(250, Math.min(tmpDelay, POLL_CAP_MS - tmpElapsed));
		this._clearTimer(this._pollTimer);
		this._pollTimer = this._setTimer(() =>
		{
			this._pollTimer = null;
			this._pollTick(pGen);
		}, tmpDelay);
	}

	/** At the cap: no more requests (poll, ticker, mirror) until Refresh. */
	_hitCap()
	{
		this._pollCapped = true;
		this._stopTimers();
		this._paintRunStatus(true);
	}

	/**
	 * Paused while the tab is hidden; resume on visibilitychange without touching the cap (hidden time counts).
	 * @param {number} pGen
	 */
	_waitVisible(pGen)
	{
		const tmpDoc = this._doc();
		if (!tmpDoc)
		{
			return;
		}
		if (this._visHandler)
		{
			tmpDoc.removeEventListener('visibilitychange', this._visHandler);
		}
		this._visHandler = () =>
		{
			if (tmpDoc.hidden)
			{
				return;
			}
			tmpDoc.removeEventListener('visibilitychange', this._visHandler);
			this._visHandler = null;
			this._pollTick(pGen);
		};
		tmpDoc.addEventListener('visibilitychange', this._visHandler);
	}

	/**
	 * The 1 s elapsed ticker: writes only #FCG-Elapsed textContent; dies with the poll guards, the cap, or terminal.
	 * @param {number} pGen
	 */
	_startTicker(pGen)
	{
		if (this._tickTimer)
		{
			return;
		}
		const fTick = () =>
		{
			this._tickTimer = null;
			if (!this._pollAlive(pGen) || this._pollCapped || !this._phase || this._phase.Terminal)
			{
				return;
			}
			if (this._capReached())
			{
				this._hitCap();
				return;
			}
			this._writeElapsed();
			this._tickTimer = this._setTimer(fTick, TICK_MS);
		};
		this._tickTimer = this._setTimer(fTick, TICK_MS);
	}

	/** Write the elapsed time into #FCG-Elapsed (textContent only). */
	_writeElapsed()
	{
		const tmpEl = this._el('FCG-Elapsed');
		if (tmpEl && this._phase && this._phase.ElapsedFrom)
		{
			tmpEl.textContent = this._fmtElapsed(this._serverNow() - this._phase.ElapsedFrom);
		}
	}

	/**
	 * Explicit Refresh (also the way out of the cap): fetch now and restart polling.
	 * @return {Promise<void>}
	 */
	refreshSession()
	{
		if (this.state.Mode !== 'session')
		{
			return Promise.resolve();
		}
		return this._openSession(true);
	}

	// ── Session payload → regions ──────────────────────────────────────────────────────────────────────────────

	/**
	 * @param {any} pPayload
	 * @return {Array<any>} messages in Sequence order
	 */
	_messages(pPayload)
	{
		const tmpList = (pPayload && Array.isArray(pPayload.Messages)) ? pPayload.Messages.slice() : [];
		tmpList.sort((pA, pB) => (Number(pA && pA.Sequence) || 0) - (Number(pB && pB.Sequence) || 0));
		return tmpList;
	}

	/**
	 * @param {any} pMessage
	 * @return {boolean}
	 */
	_isAssistant(pMessage) { return String((pMessage && pMessage.Role) || '').toLowerCase() === 'assistant'; }

	/**
	 * The thread signature (§5): Request.Status, message count, last message Status + id.
	 * @param {any} pPayload
	 * @return {string}
	 */
	_threadSignature(pPayload)
	{
		const tmpMessages = this._messages(pPayload);
		const tmpLast = tmpMessages[tmpMessages.length - 1] || {};
		const tmpSession = (pPayload && pPayload.Session) || {};
		return [ tmpSession.Status || '', tmpMessages.length, tmpLast.Status || '', tmpLast.IDRequestMessage || '' ].join('|');
	}

	/**
	 * Map a GET Session payload to the client run phase (contract D10), against the server clock.
	 * @param {any} pPayload
	 * @param {number} [pNowMs] - server-clock now (defaults to the skew-corrected clock)
	 * @return {{ Key: string, Label: string, Detail: string, Terminal: boolean, Active: boolean, Cancel: boolean, SubmitAgain: boolean, TryAgain: boolean, ElapsedFrom: number, StageLabel: string }}
	 */
	runPhase(pPayload, pNowMs)
	{
		const tmpPayload = pPayload || {};
		const tmpSession = tmpPayload.Session || {};
		const tmpRun = tmpPayload.Run || null;
		const tmpNow = (Number(pNowMs) > 0) ? Number(pNowMs) : (this._ms(tmpPayload.ServerNow) || this._serverNow());
		const tmpMessages = this._messages(tmpPayload);
		const tmpLast = tmpMessages[tmpMessages.length - 1] || null;
		/** @type {any} */
		let tmpLastUser = null;
		tmpMessages.forEach((pMessage) =>
		{
			if (!this._isAssistant(pMessage))
			{
				tmpLastUser = pMessage;
			}
		});
		let tmpStatus = String(tmpSession.Status || '');
		if (tmpLast && this._isAssistant(tmpLast) && tmpLast.Status)
		{
			tmpStatus = String(tmpLast.Status);
		}
		else if (tmpLast && !this._isAssistant(tmpLast) && (tmpMessages.length > 1))
		{
			// a follow-up was posted and its assistant turn is not there yet
			tmpStatus = 'Pending';
		}
		const tmpTurnStart = this._ms(tmpLastUser && tmpLastUser.CreateDate) || this._ms(tmpSession.CreateDate);
		const tmpStage = tmpRun && tmpRun.StageLabel ? String(tmpRun.StageLabel) : '';
		/**
		 * @param {string} pKey
		 * @param {string} pLabel
		 * @param {any} [pExtra]
		 * @return {any}
		 */
		const fPhase = (pKey, pLabel, pExtra) => Object.assign(
			{ Key: pKey, Label: pLabel, Detail: '', Terminal: false, Active: false, Cancel: false, SubmitAgain: false, TryAgain: false, ElapsedFrom: 0, StageLabel: '' },
			pExtra || {});
		if (tmpStatus === 'Answered')
		{
			return fPhase('answered', 'Answered', { Terminal: true });
		}
		if (tmpStatus === 'Failed')
		{
			return fPhase('failed', 'This run failed.', { Terminal: true, TryAgain: true });
		}
		if (tmpStatus === 'Cancelled')
		{
			return fPhase('cancelled', 'Cancelled.', { Terminal: true });
		}
		if (tmpRun && tmpRun.CancelRequested && !tmpRun.Claimed)
		{
			return fPhase('cancelled-unstarted', 'Cancelled (never started).', { Terminal: true, SubmitAgain: true });
		}
		if (tmpRun && tmpRun.Zombie)
		{
			return fPhase('notpickedup', 'Not picked up — the node was busy or offline.',
				{ Terminal: true, SubmitAgain: true, Detail: 'Plansheet runs one request at a time and never picks a run up later. Submit it again once the node is free.' });
		}
		const tmpClaimedAt = (tmpRun && this._ms(tmpRun.ClaimedAt)) || tmpTurnStart;
		if (tmpRun && tmpRun.CancelRequested && tmpRun.Claimed)
		{
			return fPhase('cancelling', 'Cancelling… it may still finish.',
				{ Active: true, ElapsedFrom: tmpClaimedAt, StageLabel: tmpStage, Detail: 'The node stops at its next heartbeat; a run that is nearly done can still complete.' });
		}
		if (tmpRun && tmpRun.Claimed)
		{
			const tmpBeat = this._ms(tmpRun.HeartbeatDate) || tmpClaimedAt;
			if (tmpBeat && ((tmpNow - tmpBeat) >= STALL_MS))
			{
				return fPhase('stalled', 'Stalled?',
					{ Active: true, Cancel: true, ElapsedFrom: tmpClaimedAt, StageLabel: tmpStage, Detail: `No heartbeat from the node for ${Math.floor((tmpNow - tmpBeat) / 60000)} min.` });
			}
			return fPhase('working', 'Working…', { Active: true, Cancel: true, ElapsedFrom: tmpClaimedAt, StageLabel: tmpStage });
		}
		const tmpAge = tmpTurnStart ? (tmpNow - tmpTurnStart) : 0;
		if (tmpAge < STARTING_MS)
		{
			return fPhase('starting', 'Starting…', { Active: true, ElapsedFrom: tmpTurnStart });
		}
		return fPhase('waiting', 'Waiting for a worker…', { Active: true, Cancel: true, ElapsedFrom: tmpTurnStart, Detail: 'No plansheet node has picked this up yet.' });
	}

	/**
	 * Apply a fetched session: thread/next steps only on a signature change, run status only when its state changed,
	 * the follow-up only by toggling Send, then the mirror.
	 * @param {any} pPayload
	 */
	_applySessionPayload(pPayload)
	{
		const tmpPayload = pPayload || {};
		this._session = tmpPayload;
		const tmpServerNow = this._ms(tmpPayload.ServerNow);
		if (tmpServerNow)
		{
			this._skew = tmpServerNow - this._now();
		}
		this._phase = this.runPhase(tmpPayload);
		const tmpSig = this._threadSignature(tmpPayload);
		if (tmpSig !== this._threadSig)
		{
			this._threadSig = tmpSig;
			this._paintThread();
			this._paintNextSteps();
		}
		// The node was busy with THIS request and it has finished: the stale NodeBusy no longer applies.
		const tmpIDRequest = Number(tmpPayload.Session && tmpPayload.Session.IDRequest) || 0;
		if (this._phase.Terminal && this._nodeBusy && (Number(this._nodeBusy.IDRequest) === tmpIDRequest))
		{
			this._nodeBusy = null;
		}
		this._paintSessionHeader();
		this._paintRunStatus();
		this._syncFollowUpLock();
		this._maybeMirror(tmpPayload);
	}

	/**
	 * The text a user turn shows (C6: Display.UserText from the façade; stripped Content otherwise).
	 * @param {any} pMessage
	 * @return {string}
	 */
	_userText(pMessage)
	{
		if (pMessage && pMessage.Display && (typeof pMessage.Display.UserText === 'string'))
		{
			return pMessage.Display.UserText;
		}
		return this._stripSteering(pMessage && pMessage.Content);
	}

	/**
	 * @param {any} pMessage
	 * @return {any} the reply metadata object, or null
	 */
	_meta(pMessage)
	{
		let tmpMeta = pMessage ? pMessage.Metadata : null;
		if (typeof tmpMeta === 'string')
		{
			try
			{
				tmpMeta = tmpMeta.trim() ? JSON.parse(tmpMeta) : null;
			}
			catch (pError)
			{
				tmpMeta = null;
			}
		}
		return (tmpMeta && (typeof tmpMeta === 'object')) ? tmpMeta : null;
	}

	/** Remember which <details> are open so a thread repaint restores them (keyed by turn index). */
	_captureDetails()
	{
		const tmpThread = this._el('FCG-Thread');
		if (!tmpThread || (typeof tmpThread.querySelectorAll !== 'function'))
		{
			return;
		}
		Array.prototype.forEach.call(tmpThread.querySelectorAll('details[data-fcg-det]'), (pDetails) =>
		{
			this._openDetails[pDetails.getAttribute('data-fcg-det')] = !!pDetails.open;
		});
	}

	/** Repaint #FCG-Thread (restores open <details>). */
	_paintThread()
	{
		if (!this._has('#FCG-Thread'))
		{
			return;
		}
		this._captureDetails();
		this._assign('#FCG-Thread', this._threadHTML());
	}

	/** @return {string} the thread (turns in Sequence order); rebuilds the copy index */
	_threadHTML()
	{
		this._copyables = [];
		const tmpPayload = this._session;
		if (!tmpPayload)
		{
			return `<div class="fcg-note"><i class="fas fa-circle-notch fa-spin"></i> Loading the session…</div>`;
		}
		const tmpMessages = this._messages(tmpPayload);
		if (!tmpMessages.length)
		{
			return `<div class="fcg-note"><i class="fas fa-paper-plane"></i> The request was submitted; its first turn is not in plansheet yet.</div>`;
		}
		let tmpSeenUser = false;
		return tmpMessages.map((pMessage, pIndex) =>
		{
			const tmpFirstUser = !this._isAssistant(pMessage) && !tmpSeenUser;
			if (!this._isAssistant(pMessage))
			{
				tmpSeenUser = true;
			}
			return this._turnHTML(pMessage, pIndex, tmpFirstUser);
		}).join('');
	}

	/**
	 * One turn. User turns render Display.UserText (the full prompt only in an escaped, collapsed <pre>); assistant
	 * turns render their Content strictly plus the metadata chips (copy indexes offset into this._copyables).
	 * @param {any} pMessage
	 * @param {number} pIndex
	 * @param {boolean} pFirstUser
	 * @return {string}
	 */
	_turnHTML(pMessage, pIndex, pFirstUser)
	{
		const tmpIndex = Number(pIndex) || 0;
		const tmpWhen = this._dateTime(pMessage.CreateDate);
		const fOpen = (pKind) => (this._openDetails[`${tmpIndex}-${pKind}`] ? ' open' : '');
		if (!this._isAssistant(pMessage))
		{
			const tmpText = this._userText(pMessage);
			const tmpBody = String(tmpText).trim()
				? this._md(tmpText)
				: `<div class="fcg-muted">No request text (files only).</div>`;
			const tmpFull = String(pMessage.Content == null ? '' : pMessage.Content);
			const tmpDetails = (tmpFull && (tmpFull.trim() !== String(tmpText).trim()))
				? `<details class="fcg-det" data-fcg-det="${tmpIndex}-prompt"${fOpen('prompt')}><summary>Full prompt sent</summary><pre class="fcg-pre">${this._esc(tmpFull)}</pre></details>`
				: '';
			return `<article class="fcg-turn fcg-turn-user" data-fcg-turn="${tmpIndex}">`
				+ `<header class="fcg-turn-h"><span class="fcg-turn-who"><i class="fas fa-user"></i> ${pFirstUser ? 'Request' : 'Follow-up'}</span><span class="fcg-turn-when">${this._esc(tmpWhen)}</span></header>`
				+ `<div class="fcg-turn-body fcg-md">${tmpBody}</div>${this._attachmentsHTML(pMessage)}${tmpDetails}</article>`;
		}
		const tmpStatus = String(pMessage.Status || '');
		const tmpStatusMeta = SESSION_STATUS_META[tmpStatus] || { Chip: tmpStatus || 'Pending', Cls: 'fcg-st-run' };
		let tmpBody = '';
		if (tmpStatus === 'Answered')
		{
			tmpBody = String(pMessage.Content || '').trim() ? this._md(pMessage.Content) : `<div class="fcg-muted">(empty reply)</div>`;
		}
		else if (tmpStatus === 'Failed')
		{
			tmpBody = `<div class="fcg-muted">This turn failed.</div>${String(pMessage.Content || '').trim() ? this._md(pMessage.Content) : ''}`;
		}
		else if (tmpStatus === 'Cancelled')
		{
			tmpBody = `<div class="fcg-muted">Cancelled.</div>`;
		}
		else
		{
			tmpBody = `<div class="fcg-muted"><i class="fas fa-circle-notch fa-spin"></i> Working on it…</div>`;
		}
		return `<article class="fcg-turn fcg-turn-assistant" data-fcg-turn="${tmpIndex}">`
			+ `<header class="fcg-turn-h"><span class="fcg-turn-who"><i class="fas fa-wand-sparkles"></i> Plansheet</span>`
			+ `<span class="fcg-chip ${tmpStatusMeta.Cls}">${this._esc(tmpStatusMeta.Chip)}</span><span class="fcg-turn-when">${this._esc(tmpWhen)}</span></header>`
			+ `<div class="fcg-turn-body fcg-md">${tmpBody}</div>${this._metaHTML(pMessage)}${this._attachmentsHTML(pMessage)}</article>`;
	}

	/**
	 * Reply metadata chips via the Markdown module; its data-fcg-copy indexes are offset into this._copyables.
	 * @param {any} pMessage
	 * @return {string}
	 */
	_metaHTML(pMessage)
	{
		const tmpMeta = this._meta(pMessage);
		if (!tmpMeta || (typeof this.md.renderReplyMetadata !== 'function'))
		{
			return '';
		}
		let tmpResult = null;
		try
		{
			tmpResult = this.md.renderReplyMetadata(tmpMeta);
		}
		catch (pError)
		{
			return `<details class="fcg-det"><summary>Details</summary><pre class="fcg-pre">${this._esc(JSON.stringify(tmpMeta, null, 2))}</pre></details>`;
		}
		const tmpBase = this._copyables.length;
		const tmpHTML = String((tmpResult && tmpResult.HTML) || '').replace(/data-fcg-copy="(\d+)"/g, (pMatch, pIdx) => `data-fcg-copy="${tmpBase + Number(pIdx)}"`);
		const tmpCopyables = (tmpResult && Array.isArray(tmpResult.Copyables)) ? tmpResult.Copyables : [];
		tmpCopyables.forEach((pText) => this._copyables.push(String(pText)));
		return tmpHTML ? `<div class="fcg-meta">${tmpHTML}</div>` : '';
	}

	/**
	 * @param {any} pMessage
	 * @return {string} escaped attachment chips
	 */
	_attachmentsHTML(pMessage)
	{
		const tmpList = (pMessage && Array.isArray(pMessage.Attachments)) ? pMessage.Attachments : [];
		if (!tmpList.length)
		{
			return '';
		}
		return `<div class="fcg-attach">${tmpList.map((pItem) =>
		{
			const tmpName = (pItem && (typeof pItem === 'object')) ? (pItem.Name || pItem.FileName || pItem.Title || 'attachment') : String(pItem);
			return `<span class="fcg-chip"><i class="fas fa-paperclip"></i> ${this._esc(tmpName)}</span>`;
		}).join('')}</div>`;
	}

	/**
	 * Repaint #FCG-SessionHeader when its inputs changed (or always, when forced by a mirror update).
	 * @param {boolean} [pForce]
	 */
	_paintSessionHeader(pForce)
	{
		if ((this.state.Mode !== 'session') || !this._has('#FCG-SessionHeader'))
		{
			return;
		}
		const tmpKey = `${this._threadSig}|${this._phase ? this._phase.Key : ''}`;
		if (!pForce && (tmpKey === this._headKey))
		{
			return;
		}
		this._headKey = tmpKey;
		this._assign('#FCG-SessionHeader', this._sessionHeaderHTML());
	}

	/** @return {string} the session header (form, flow, status, started, mirror, blueprint) */
	_sessionHeaderHTML()
	{
		const tmpBack = `<a class="fcg-back" href="#/FormConcierge"><i class="fas fa-arrow-left"></i> Form Concierge</a>`;
		const tmpPayload = this._session;
		if (!tmpPayload)
		{
			return `${tmpBack}<h2 class="fcg-shead-t">Session #${Number(this.state.IDRequest) || 0}</h2>`;
		}
		const tmpSession = tmpPayload.Session || {};
		const tmpForm = tmpPayload.Form || {};
		const tmpFlow = String(tmpSession.Flow || '').toLowerCase();
		const tmpFlowChip = `<span class="fcg-chip fcg-chip-flow">${this._esc(FLOW_CHIP[tmpFlow] || tmpSession.Flow || 'Session')}</span>`;
		const tmpPhaseMeta = (this._phase && PHASE_META[this._phase.Key]) || { Chip: tmpSession.Status || '', Cls: 'fcg-st-run' };
		const tmpStatusChip = tmpPhaseMeta.Chip ? `<span class="fcg-chip ${tmpPhaseMeta.Cls}">${this._esc(tmpPhaseMeta.Chip)}</span>` : '';
		const tmpNumber = Number(tmpSession.RequestNumber) || 0;
		const tmpStarted = this._relTime(tmpSession.CreateDate);
		const tmpFacts = [ tmpNumber ? `Request #${tmpNumber}` : '', tmpStarted ? `started ${this._esc(tmpStarted)}` : '' ].filter(Boolean).join(' · ');
		return `${tmpBack}`
			+ `<div class="fcg-shead-row"><h2 class="fcg-shead-t">${this._esc(tmpSession.FormName || tmpForm.Name || 'Form')}</h2>${tmpFlowChip}${tmpStatusChip}</div>`
			+ `<div class="fcg-shead-sub">${tmpFacts}${this._blueprintLinkHTML()}</div>`
			+ `<div class="fcg-shead-mirror">${this._mirrorHTML(Number(tmpSession.IDRequest) || 0)}</div>`;
	}

	/** @return {string} the latest blueprint share link (https://plansheet.io/ only), or '' */
	_blueprintLinkHTML()
	{
		let tmpURL = '';
		this._messages(this._session).forEach((pMessage) =>
		{
			const tmpMeta = this._isAssistant(pMessage) ? this._meta(pMessage) : null;
			if (tmpMeta && tmpMeta.Blueprint && (typeof tmpMeta.Blueprint.ShareURL === 'string'))
			{
				tmpURL = tmpMeta.Blueprint.ShareURL;
			}
		});
		if (!tmpURL || (tmpURL.indexOf('https://plansheet.io/') !== 0) || (typeof this.md.linkHTML !== 'function'))
		{
			return '';
		}
		try
		{
			return ` · <span class="fcg-shead-link"><i class="fas fa-compass-drafting"></i> ${this.md.linkHTML(tmpURL, 'Open blueprint')}</span>`;
		}
		catch (pError)
		{
			return '';
		}
	}

	/**
	 * @param {number} pIDRequest
	 * @return {string} mirror state for the session header
	 */
	_mirrorHTML(pIDRequest)
	{
		const tmpReady = this._mirrorReady;
		if (!tmpReady)
		{
			return `<span class="fcg-mirror"><i class="fas fa-circle-notch fa-spin"></i> Checking the Headlight mirror…</span>`;
		}
		if (!tmpReady.Ready || !this._mirror)
		{
			const tmpReason = String(tmpReady.Reason || '').trim();
			// The Mirror module's reason may already be the whole sentence.
			const tmpText = /^not mirrored/i.test(tmpReason)
				? tmpReason
				: `Not mirrored to Headlight: ${tmpReason || 'the FormConcierge-Session form type isn\'t installed in this environment'}`;
			return `<span class="fcg-mirror fcg-mirror-off"><i class="fas fa-circle-info"></i> ${this._esc(tmpText)}</span>`;
		}
		const tmpEntry = this._mirrorState[pIDRequest];
		if (tmpEntry && (Number(tmpEntry.IDDocument) > 0))
		{
			return `<span class="fcg-mirror fcg-mirror-on"><i class="fas fa-link"></i> Mirrored to Headlight · `
				+ `<a href="#" onclick="_Pict.providers.FormConcierge.openMirrorDocument(${Number(pIDRequest) || 0}); return false;">Open in Headlight</a></span>`;
		}
		if (tmpEntry && tmpEntry.Skipped && tmpEntry.Reason)
		{
			return `<span class="fcg-mirror fcg-mirror-warn"><i class="fas fa-triangle-exclamation"></i> The Headlight mirror did not update: ${this._esc(tmpEntry.Reason)}</span>`;
		}
		return `<span class="fcg-mirror"><i class="fas fa-circle-notch fa-spin"></i> Mirroring to Headlight…</span>`;
	}

	/**
	 * Repaint #FCG-RunStatus when the phase/stage/cap/error changed (or forced).
	 * @param {boolean} [pForce]
	 */
	_paintRunStatus(pForce)
	{
		if (!this._has('#FCG-RunStatus'))
		{
			return;
		}
		const tmpPhase = this._phase;
		const tmpKey = [ tmpPhase ? tmpPhase.Key : '', tmpPhase ? tmpPhase.StageLabel : '', tmpPhase ? tmpPhase.Detail : '', this._pollCapped, this._pollError ].join('|');
		if (!pForce && (tmpKey === this._runKey))
		{
			return;
		}
		this._runKey = tmpKey;
		this._assign('#FCG-RunStatus', this._runStatusHTML());
	}

	/** @return {string} the run status line + its actions */
	_runStatusHTML()
	{
		const tmpPhase = this._phase;
		const tmpError = this._pollError ? `<div class="fcg-run-err"><i class="fas fa-rotate"></i> Couldn't refresh: ${this._esc(this._pollError)}</div>` : '';
		if (!tmpPhase)
		{
			return tmpError;
		}
		const tmpMeta = PHASE_META[tmpPhase.Key] || PHASE_META.working;
		const tmpButtons = [];
		if (this._pollCapped && !tmpPhase.Terminal)
		{
			return `<div class="fcg-run fcg-st-warn"><i class="fas fa-pause"></i> <b>Still running.</b> Auto-refresh stopped after an hour.`
				+ ` <button type="button" class="fcg-btn fcg-btn-sm" onclick="_Pict.providers.FormConcierge.refreshSession(); return false;"><i class="fas fa-rotate"></i> Refresh</button></div>${tmpError}`;
		}
		if (tmpPhase.Cancel)
		{
			tmpButtons.push(`<button type="button" class="fcg-btn fcg-btn-sm" onclick="_Pict.providers.FormConcierge.cancelRun(); return false;"><i class="fas fa-ban"></i> Cancel</button>`);
		}
		if (tmpPhase.SubmitAgain)
		{
			tmpButtons.push(`<button type="button" class="fcg-btn fcg-btn-sm fcg-btn-primary" onclick="_Pict.providers.FormConcierge.resubmit(); return false;"><i class="fas fa-paper-plane"></i> Submit again</button>`);
		}
		if (tmpPhase.TryAgain)
		{
			tmpButtons.push(`<button type="button" class="fcg-btn fcg-btn-sm fcg-btn-primary" onclick="_Pict.providers.FormConcierge.resubmit(); return false;"><i class="fas fa-rotate-right"></i> Try again</button>`);
		}
		const tmpSpin = tmpPhase.Active && (tmpPhase.Key !== 'stalled');
		const tmpIcon = tmpSpin ? 'fa-circle-notch fa-spin' : tmpMeta.Icon;
		const tmpElapsed = (tmpPhase.Active && tmpPhase.ElapsedFrom)
			? ` <span id="FCG-Elapsed" class="fcg-elapsed">${this._fmtElapsed(this._serverNow() - tmpPhase.ElapsedFrom)}</span>`
			: '';
		const tmpStage = tmpPhase.StageLabel ? ` <span class="fcg-stage">${this._esc(tmpPhase.StageLabel)}</span>` : '';
		const tmpDetail = tmpPhase.Detail ? `<div class="fcg-run-sub">${this._esc(tmpPhase.Detail)}</div>` : '';
		return `<div class="fcg-run ${tmpMeta.Cls}"><div class="fcg-run-main"><i class="fas ${tmpIcon}"></i> <b>${this._esc(tmpPhase.Label)}</b>${tmpElapsed}${tmpStage}`
			+ `${tmpButtons.length ? `<span class="fcg-run-actions">${tmpButtons.join('')}</span>` : ''}</div>${tmpDetail}</div>${tmpError}`;
	}

	/** Repaint #FCG-NextSteps. */
	_paintNextSteps() { this._assign('#FCG-NextSteps', this._nextStepsHTML()); }

	/** @return {string} Build & deliver after a blueprint answer; Generate examples once the form is delivered */
	_nextStepsHTML()
	{
		const tmpPayload = this._session;
		if (!tmpPayload || !tmpPayload.Session)
		{
			return '';
		}
		const tmpFlow = String(tmpPayload.Session.Flow || '').toLowerCase();
		const tmpForm = tmpPayload.Form || {};
		const tmpIDProject = Number(tmpForm.IDProject || tmpPayload.Session.IDProject) || 0;
		if (!tmpIDProject)
		{
			return '';
		}
		const tmpAnswered = this._messages(tmpPayload).filter((pMessage) => this._isAssistant(pMessage) && (pMessage.Status === 'Answered'));
		const tmpBlueprint = tmpAnswered.some((pMessage) =>
		{
			const tmpMeta = this._meta(pMessage);
			return !!(tmpMeta && tmpMeta.Blueprint);
		});
		const tmpPushed = this._isDelivered(tmpForm) || tmpAnswered.some((pMessage) =>
		{
			const tmpMeta = this._meta(pMessage);
			return !!(tmpMeta && tmpMeta.HeadlightForm && (tmpMeta.HeadlightForm.Pushed === true));
		});
		const tmpSteps = [];
		if ([ 'create', 'update', 'author', 'modify' ].indexOf(tmpFlow) >= 0 && tmpBlueprint)
		{
			tmpSteps.push(`<a class="fcg-btn fcg-btn-primary" href="#/FormConcierge/build/${tmpIDProject}"><i class="fas fa-truck-ramp-box"></i> Build & deliver the form</a>`);
		}
		if (tmpPushed)
		{
			tmpSteps.push(`<a class="fcg-btn" href="#/FormConcierge/examples/${tmpIDProject}"><i class="fas fa-vials"></i> Generate examples</a>`);
		}
		if (!tmpSteps.length)
		{
			return '';
		}
		return `<div class="fcg-next"><div class="fcg-next-h">Next steps</div><div class="fcg-next-actions">${tmpSteps.join('')}</div></div>`;
	}

	// ── Painting: shells ───────────────────────────────────────────────────────────────────────────────────────

	/** Paint whatever the current state calls for (notice, home, or session). */
	_render()
	{
		if (this._locked)
		{
			this._paintNotice('locked');
			return;
		}
		if (this.state.Loading)
		{
			this._paintNotice('loading');
			return;
		}
		if (!this._statusLoaded)
		{
			this._paintNotice(this.state.Error ? 'error' : 'loading');
			return;
		}
		if (!this._isReady())
		{
			this._paintNotice(this._status && this._status.Unreachable ? 'unreachable' : (this._status && this._status.Configured === false ? 'notconfigured' : 'notallowed'));
			return;
		}
		if (this.state.Mode === 'session')
		{
			this._paintSession();
			return;
		}
		this._paintHome();
	}

	/** Attach the one delegated click listener (copy buttons) to the current host element. */
	_attachHostListener()
	{
		const tmpDoc = this._doc();
		const tmpHost = tmpDoc ? tmpDoc.querySelector(this._mount) : null;
		if (!tmpHost || (tmpHost === this._hostListenerEl))
		{
			return;
		}
		this._hostListenerEl = tmpHost;
		tmpHost.addEventListener('click', (pEvent) => this._onHostClick(pEvent));
	}

	/**
	 * Delegated click: a [data-fcg-copy="<index>"] button copies this._copyables[index] (never markup-carried text).
	 * @param {any} pEvent
	 */
	_onHostClick(pEvent)
	{
		const tmpTarget = /** @type {any} */ (pEvent && pEvent.target);
		const tmpButton = (tmpTarget && (typeof tmpTarget.closest === 'function')) ? tmpTarget.closest('[data-fcg-copy]') : null;
		if (!tmpButton)
		{
			return;
		}
		if (typeof pEvent.preventDefault === 'function')
		{
			pEvent.preventDefault();
		}
		const tmpRaw = String(tmpButton.getAttribute('data-fcg-copy') || '');
		const tmpIndex = /^\d{1,6}$/.test(tmpRaw) ? Number(tmpRaw) : -1;
		const tmpText = (tmpIndex >= 0) ? this._copyables[tmpIndex] : undefined;
		if (typeof tmpText === 'string')
		{
			this._copyText(tmpText);
		}
	}

	/**
	 * Copy text to the clipboard (async API, else a hidden-textarea fallback).
	 * @param {string} pText
	 */
	_copyText(pText)
	{
		const tmpWindow = /** @type {any} */ (this._win());
		const tmpClipboard = tmpWindow && tmpWindow.navigator && tmpWindow.navigator.clipboard;
		if (tmpClipboard && (typeof tmpClipboard.writeText === 'function'))
		{
			tmpClipboard.writeText(pText).then(() => this._toast('Copied to the clipboard.', 'success'), () => this._copyFallback(pText));
			return;
		}
		this._copyFallback(pText);
	}

	/**
	 * @param {string} pText
	 */
	_copyFallback(pText)
	{
		const tmpDoc = this._doc();
		if (!tmpDoc)
		{
			return;
		}
		const tmpArea = tmpDoc.createElement('textarea');
		tmpArea.value = pText;
		tmpArea.setAttribute('readonly', '');
		tmpArea.style.position = 'fixed';
		tmpArea.style.left = '-9999px';
		tmpDoc.body.appendChild(tmpArea);
		tmpArea.select();
		let tmpOK = false;
		try
		{
			tmpOK = tmpDoc.execCommand('copy');
		}
		catch (pError)
		{
			tmpOK = false;
		}
		tmpDoc.body.removeChild(tmpArea);
		this._toast(tmpOK ? 'Copied to the clipboard.' : 'Copy failed; select the text and copy it by hand.', tmpOK ? 'success' : 'warning');
	}

	/**
	 * Paint the host with the header + one notice (loading / locked / error / not configured / unreachable).
	 * @param {string} pKind
	 */
	_paintNotice(pKind)
	{
		if (!this._has(this._mount))
		{
			return;
		}
		this._assign(this._mount, `<div class="fcg" data-fcg-mode="notice"><div id="FCG-Header">${this._headerHTML()}</div>${this._noticeHTML(pKind)}</div>`);
		this._paintedMode = `notice:${pKind}`;
		this._attachHostListener();
	}

	/**
	 * @param {string} pKind
	 * @return {string}
	 */
	_noticeHTML(pKind)
	{
		const tmpRetry = `<button type="button" class="fcg-btn fcg-btn-sm" onclick="_Pict.providers.FormConcierge.loadAsync(); return false;"><i class="fas fa-rotate"></i> Retry</button>`;
		switch (pKind)
		{
			case 'locked':
				return `<div class="fcg-note fcg-note-locked"><i class="fas fa-lock"></i> <div><b>Form Concierge is for platform super-users only.</b><br>It drives the plansheet form pipeline on Headlight's behalf.</div></div>`;
			case 'notallowed':
				return `<div class="fcg-note fcg-note-locked"><i class="fas fa-lock"></i> <div><b>The server did not allow this session.</b><br>Form Concierge accepts only signed-in platform super-users.</div></div>`;
			case 'notconfigured':
				return `<div class="fcg-note fcg-note-warn"><i class="fas fa-plug-circle-xmark"></i> <div><b>Form Concierge isn't configured on this server.</b><br>`
					+ `Production needs FORM_CONCIERGE_PLANSHEET_TOKEN and FORM_CONCIERGE_ENVIRONMENT; local dev needs Plansheet.Enabled, Token and Environment in .headlight.config.json. Restart after setting them.</div></div>`;
			case 'unreachable':
				return `<div class="fcg-note fcg-note-warn"><i class="fas fa-plug-circle-exclamation"></i> <div><b>The Form Concierge service didn't answer.</b><br>`
					+ `It runs only in the deployed config-app image and under services/proxy_server serve. ${tmpRetry}</div></div>`;
			case 'error':
				return `<div class="fcg-note fcg-note-error"><i class="fas fa-circle-exclamation"></i> <div><b>Form Concierge couldn't load.</b><br>${this._esc(this.state.Error)} ${tmpRetry}</div></div>`;
			default:
				return `<div class="fcg-note"><i class="fas fa-circle-notch fa-spin"></i> Loading Form Concierge…</div>`;
		}
	}

	/** @return {string} the title row with the façade status pill */
	_headerHTML()
	{
		const tmpStatus = this._status;
		let tmpPill = `<span class="fcg-pill">Checking…</span>`;
		if (this._locked)
		{
			tmpPill = `<span class="fcg-pill fcg-st-off">Locked</span>`;
		}
		else if (this._statusLoaded && tmpStatus)
		{
			if (tmpStatus.Unreachable)
			{
				tmpPill = `<span class="fcg-pill fcg-st-err">Unreachable</span>`;
			}
			else if (!tmpStatus.Configured)
			{
				tmpPill = `<span class="fcg-pill fcg-st-warn">Not configured</span>`;
			}
			else if (!tmpStatus.Allowed)
			{
				tmpPill = `<span class="fcg-pill fcg-st-off">Not allowed</span>`;
			}
			else if (!this._taskFor('create') || !this._taskFor('build'))
			{
				tmpPill = `<span class="fcg-pill fcg-st-warn">Tasks missing</span>`;
			}
			else
			{
				tmpPill = `<span class="fcg-pill fcg-st-ok">Ready</span>`;
			}
		}
		const tmpEnv = (tmpStatus && tmpStatus.Environment && this._isReady()) ? `<span class="fcg-pill fcg-pill-env">${this._esc(tmpStatus.Environment)}</span>` : '';
		const tmpBusy = (this._nodeBusy && this._isReady()) ? `<span class="fcg-pill fcg-st-warn" title="The plansheet node is working on another request">Node busy</span>` : '';
		return `<div class="fcg-head"><div class="fcg-head-l"><h1 class="fcg-title"><i class="fas fa-wand-sparkles"></i> Form Concierge</h1>`
			+ `<p class="fcg-sub">Drop files, describe a form, and have the plansheet form pipeline create it, update it, build and deliver it, or seed examples for it.</p></div>`
			+ `<div class="fcg-head-r">${tmpPill}${tmpEnv}${tmpBusy}</div></div>`;
	}

	/** Repaint #FCG-Header. */
	_paintHeader() { this._assign('#FCG-Header', this._headerHTML()); }

	/** Paint the Home skeleton (header, flow tabs, composer regions, sessions) and mount the picker + editor. */
	_paintHome()
	{
		if (!this._has(this._mount))
		{
			return;
		}
		const tmpFlow = this.state.Flow;
		const tmpDropHandlers = `ondragover="_Pict.providers.FormConcierge.onDragOver(event,'composer')" ondragleave="_Pict.providers.FormConcierge.onDragLeave(event,'composer')" ondrop="_Pict.providers.FormConcierge.onDrop(event,'composer')"`;
		const tmpTasksNote = (!this._taskFor('create') || !this._taskFor('build'))
			? `<div class="fcg-note fcg-note-warn"><i class="fas fa-triangle-exclamation"></i> Plansheet's form-administration tasks weren't all found${this._taskFor('create') ? '' : ' (Author is missing)'}${this._taskFor('build') ? '' : ' (Config is missing)'}. Flows that need them stay off.</div>`
			: '';
		const tmpHTML = `<div class="fcg" data-fcg-mode="home">`
			+ `<div id="FCG-Header">${this._headerHTML()}</div>${tmpTasksNote}`
			+ `<nav id="FCG-Flow" class="fcg-flow">${this._flowTabsHTML()}</nav>`
			+ `<section id="FCG-Composer" class="fcg-card">`
			+ `<div id="FCG-Target" class="fcg-target">${this._targetHTML()}</div>`
			+ `<div id="FCG-DropWrap" class="fcg-dropwrap${tmpFlow === 'build' ? ' fcg-hidden' : ''}">`
			+ `<div id="FCG-Drop" class="fcg-drop" ${tmpDropHandlers}>${this._dropInnerHTML('composer')}</div>`
			+ `<div id="FCG-Files" class="fcg-files">${this._filesHTML('composer')}</div></div>`
			+ `<div id="FCG-EditorLabel" class="fcg-label"></div>`
			+ `<div id="FCG-Editor" class="fcg-editor" ondragover="_Pict.providers.FormConcierge.onEditorDragOver(event)" ondrop="_Pict.providers.FormConcierge.onDrop(event,'composer')"><div id="FCG-Editor-Mount"></div></div>`
			+ `<div id="FCG-Guidance" class="fcg-guidance"></div>`
			+ `<div id="FCG-Go" class="fcg-go">${this._goHTML()}</div>`
			+ `</section>`
			+ `<section id="FCG-Sessions" class="fcg-sessions">${this._sessionsHTML()}</section>`
			+ `</div>`;
		this._assign(this._mount, tmpHTML);
		this._paintedMode = 'home';
		this._attachHostListener();
		this._mountPicker();
		this._mountEditor('composer');
		this._paintGuidance();
	}

	/** Paint the Session skeleton and mount the follow-up editor. */
	_paintSession()
	{
		if (!this._has(this._mount))
		{
			return;
		}
		const tmpDropHandlers = `ondragover="_Pict.providers.FormConcierge.onDragOver(event,'followup')" ondragleave="_Pict.providers.FormConcierge.onDragLeave(event,'followup')" ondrop="_Pict.providers.FormConcierge.onDrop(event,'followup')"`;
		this._runKey = '';
		this._headKey = '';
		this._threadSig = this._session ? this._threadSignature(this._session) : '';
		const tmpHTML = `<div class="fcg" data-fcg-mode="session">`
			+ `<div id="FCG-Header">${this._headerHTML()}</div>`
			+ `<section id="FCG-SessionHeader" class="fcg-shead">${this._sessionHeaderHTML()}</section>`
			+ `<section id="FCG-Thread" class="fcg-thread">${this._threadHTML()}</section>`
			+ `<section id="FCG-RunStatus" class="fcg-runwrap">${this._runStatusHTML()}</section>`
			+ `<section id="FCG-FollowUp" class="fcg-card fcg-fu">`
			+ `<div class="fcg-label">Follow up</div>`
			+ `<div id="FCG-FU-Editor" class="fcg-editor" ondragover="_Pict.providers.FormConcierge.onEditorDragOver(event)" ondrop="_Pict.providers.FormConcierge.onDrop(event,'followup')"><div id="FCG-FU-Editor-Mount"></div></div>`
			+ `<div id="FCG-FU-Drop" class="fcg-drop fcg-drop-sm" ${tmpDropHandlers}>${this._dropInnerHTML('followup')}</div>`
			+ `<div id="FCG-FU-Files" class="fcg-files">${this._filesHTML('followup')}</div>`
			+ `<div class="fcg-fu-actions"><button id="FCG-SendBtn" type="button" class="fcg-btn fcg-btn-primary" onclick="_Pict.providers.FormConcierge.sendFollowUp(); return false;"><i class="fas fa-paper-plane"></i> Send</button>`
			+ `<button id="FCG-RecheckBtn" type="button" class="fcg-linkbtn fcg-hidden" onclick="_Pict.providers.FormConcierge.recheckNode(); return false;">Check the node again</button>`
			+ `<span id="FCG-SendNote" class="fcg-go-note"></span></div>`
			+ `</section>`
			+ `<section id="FCG-NextSteps">${this._nextStepsHTML()}</section>`
			+ `</div>`;
		this._assign(this._mount, tmpHTML);
		this._paintedMode = 'session';
		this._attachHostListener();
		this._mountEditor('followup');
		this._syncFollowUpLock();
	}

	// ── Painting: home regions ─────────────────────────────────────────────────────────────────────────────────

	/** @return {string} the segmented flow links (hash routes, never JS tab state) */
	_flowTabsHTML()
	{
		const tmpFlow = this.state.Flow;
		return FLOWS.map((pFlow) =>
		{
			const tmpMeta = FLOW_META[pFlow];
			return `<a class="fcg-seg${pFlow === tmpFlow ? ' fcg-seg-on' : ''}" href="#/FormConcierge/${pFlow}"><i class="fas ${tmpMeta.Icon}"></i> ${tmpMeta.Label}</a>`;
		}).join('');
	}

	/** Repaint #FCG-Flow (links only). */
	_paintFlowTabs() { this._assign('#FCG-Flow', this._flowTabsHTML()); }

	/** @return {string} the Target region for the current flow (name input / form picker / deliver-to) */
	_targetHTML()
	{
		const tmpState = this.state;
		const tmpFlow = tmpState.Flow;
		const tmpID = Number(tmpState.IDProject) || 0;
		if (tmpFlow === 'create')
		{
			if (tmpID)
			{
				return `<div class="fcg-label">Form</div><div class="fcg-fixed"><i class="fas fa-file-lines"></i> <b>${this._esc(this._formLabel(tmpID))}</b>`
					+ ` <span class="fcg-muted">(created; finish starting it here)</span> <a class="fcg-linkbtn" href="#/FormConcierge">Start a different form</a></div>`;
			}
			return `<label class="fcg-label" for="FCG-Name">Form name <span class="fcg-muted">(optional; defaults to your first heading)</span></label>`
				+ `<input id="FCG-Name" class="fcg-input" type="text" maxlength="200" autocomplete="off" value="${this._esc(this._formName)}" placeholder="e.g. Concrete cylinder break log" oninput="_Pict.providers.FormConcierge.setFormName(this.value)">`;
		}
		const tmpPicker = `<div class="fcg-label">Form</div><div id="FCG-FormPicker" class="fcg-picker"></div><div id="FCG-TargetInfo" class="fcg-targetinfo">${this._targetInfoHTML()}</div>`;
		if (tmpFlow === 'build')
		{
			const tmpForm = tmpID
				? `<div class="fcg-label">Form</div><div class="fcg-fixed"><i class="fas fa-file-lines"></i> <b>${this._esc(this._formLabel(tmpID))}</b> <a class="fcg-linkbtn" href="#/FormConcierge/build">Pick another</a></div><div id="FCG-TargetInfo" class="fcg-targetinfo">${this._targetInfoHTML()}</div>`
				: tmpPicker;
			return `${tmpForm}<div class="fcg-deliver">`
				+ `<label class="fcg-label" for="FCG-DeliverTo">Deliver to</label>`
				+ `<input id="FCG-DeliverTo" class="fcg-input" type="text" maxlength="200" autocomplete="off" value="${this._esc(this._deliverTo)}" placeholder="Headlight environment and customer, e.g. Headlight QA, customer 10"${this._validateOnly ? ' disabled' : ''} oninput="_Pict.providers.FormConcierge.setDeliverTo(this.value)">`
				+ `<label class="fcg-check"><input id="FCG-ValidateOnly" type="checkbox"${this._validateOnly ? ' checked' : ''} onchange="_Pict.providers.FormConcierge.setValidateOnly(this.checked)"> Validate only (don't push)</label></div>`;
		}
		return tmpPicker;
	}

	/** Repaint #FCG-Target and re-mount the picker into it. */
	_paintTarget()
	{
		if (this._assign('#FCG-Target', this._targetHTML()))
		{
			this._mountPicker();
		}
	}

	/** @return {string} chips describing the selected form (status, blueprint, delivery) */
	_targetInfoHTML()
	{
		const tmpID = Number(this.state.IDProject) || 0;
		if (!tmpID)
		{
			return this._formsError ? `<div class="fcg-muted">Couldn't load the form list: ${this._esc(this._formsError)}</div>` : '';
		}
		const tmpFound = this._findForm(tmpID);
		if (!tmpFound)
		{
			return `<div class="fcg-muted">Form #${tmpID} isn't in this customer's Concierge forms.</div>`;
		}
		const tmpForm = tmpFound.Form;
		const tmpChips = [];
		if (tmpForm.Status)
		{
			tmpChips.push(`<span class="fcg-chip">${this._esc(tmpForm.Status)}</span>`);
		}
		tmpChips.push(Number(tmpForm.IDBlueprint) > 0 ? `<span class="fcg-chip fcg-st-ok">Blueprint</span>` : `<span class="fcg-chip fcg-st-off">No blueprint yet</span>`);
		const tmpHash = (tmpForm.Delivery && tmpForm.Delivery.Hash) || (tmpForm.HeadlightForm && tmpForm.HeadlightForm.Hash) || '';
		tmpChips.push(this._isDelivered(tmpForm)
			? `<span class="fcg-chip fcg-st-ok">Delivered${tmpHash ? ` · ${this._esc(tmpHash)}` : ''}</span>`
			: `<span class="fcg-chip fcg-st-off">Not delivered</span>`);
		if (!tmpFound.Indexed)
		{
			tmpChips.push(`<span class="fcg-chip fcg-st-warn">Not yet in Concierge</span>`);
		}
		return `<div class="fcg-chips">${tmpChips.join('')}</div>`;
	}

	/** Repaint #FCG-TargetInfo (holds no widget). */
	_paintTargetInfo() { this._assign('#FCG-TargetInfo', this._targetInfoHTML()); }

	/** Guidance + editor label (textContent only: the guidance may come from plansheet's PromptShape). */
	_paintGuidance()
	{
		const tmpGuidance = this._el('FCG-Guidance');
		if (tmpGuidance)
		{
			tmpGuidance.textContent = this._guidanceText();
		}
		const tmpLabel = this._el('FCG-EditorLabel');
		if (tmpLabel)
		{
			tmpLabel.textContent = (FLOW_META[this.state.Flow] || FLOW_META.create).EditorLabel;
		}
	}

	/** @return {string} the guidance line for the current flow (plain text) */
	_guidanceText()
	{
		const tmpFlow = this.state.Flow;
		const tmpTask = this._taskFor(tmpFlow);
		const tmpShape = (tmpTask && (typeof tmpTask.InputGuidance === 'string')) ? tmpTask.InputGuidance.trim() : '';
		if (tmpFlow === 'update')
		{
			return 'Describe exactly what should change. Everything you do not mention stays as it is.';
		}
		if (tmpFlow === 'examples')
		{
			return 'Optional: say how many examples you want and which scenarios. The delivered form is not rebuilt.';
		}
		if (tmpFlow === 'build')
		{
			return tmpShape || 'Builds the form configuration from the agreed blueprint, validates it, and delivers it where you say.';
		}
		return tmpShape || 'Describe the form: what it records, who fills it in, and any rules. Dropped files become the form\'s sources.';
	}

	/** Show/hide the drop zone (Build & deliver takes no files). */
	_paintDropVisibility()
	{
		const tmpWrap = this._el('FCG-DropWrap');
		if (tmpWrap && tmpWrap.classList)
		{
			tmpWrap.classList.toggle('fcg-hidden', this.state.Flow === 'build');
		}
	}

	/** Repaint #FCG-Go (the only region Busy repaints). */
	_paintGo() { this._assign('#FCG-Go', this._goHTML()); }

	/** @return {string} the Go button + busy text or the reason it is off */
	_goHTML()
	{
		const tmpState = this.state;
		const tmpMeta = FLOW_META[tmpState.Flow] || FLOW_META.create;
		if (tmpState.Busy)
		{
			return `<button type="button" class="fcg-btn fcg-btn-primary" disabled><i class="fas fa-circle-notch fa-spin"></i> ${this._esc(tmpState.BusyText || 'Working…')}</button>`;
		}
		const tmpBlock = this._goBlock();
		return `<button id="FCG-GoBtn" type="button" class="fcg-btn fcg-btn-primary"${tmpBlock.Disabled ? ' disabled' : ''} onclick="_Pict.providers.FormConcierge.go(); return false;"><i class="fas ${tmpMeta.Icon}"></i> ${tmpMeta.Go}</button>`
			+ (tmpBlock.NoteHTML ? `<span class="fcg-go-note">${tmpBlock.NoteHTML}</span>` : '');
	}

	/** @return {string} the NodeBusy explanation + Check again (and a link to that session) */
	_nodeBusyHTML()
	{
		const tmpBusy = this._nodeBusy || {};
		const tmpID = Number(tmpBusy.IDRequest) || 0;
		const tmpStage = tmpBusy.StageLabel ? ` (${this._esc(tmpBusy.StageLabel)})` : '';
		return `<i class="fas fa-hourglass-half"></i> The plansheet node is busy${tmpID ? ` with request <a href="#/FormConcierge/Session/${tmpID}">#${tmpID}</a>` : ''}${tmpStage}. `
			+ `It runs one request at a time and never picks one up later, so this stays off until it finishes. `
			+ `<button type="button" class="fcg-linkbtn" onclick="_Pict.providers.FormConcierge.recheckNode(); return false;">Check again</button>`;
	}

	/**
	 * Why Go is off right now (non-text reasons; empty text is checked on click).
	 * @return {{ Disabled: boolean, NoteHTML: string }}
	 */
	_goBlock()
	{
		const tmpState = this.state;
		const tmpFlow = tmpState.Flow;
		const tmpID = Number(tmpState.IDProject) || 0;
		const fBlock = (pHTML) => ({ Disabled: true, NoteHTML: pHTML });
		if (!this._isReady())
		{
			return fBlock('Form Concierge is not ready.');
		}
		if (!this._taskFor(tmpFlow))
		{
			return fBlock(`Plansheet's ${(tmpFlow === 'build') || (tmpFlow === 'examples') ? 'form configuration' : 'form authoring'} task is not available.`);
		}
		if (this._nodeBusy)
		{
			return fBlock(this._nodeBusyHTML());
		}
		if ((tmpFlow !== 'create') && !tmpID)
		{
			return fBlock(tmpFlow === 'examples' ? 'Pick a delivered form.' : 'Pick a form.');
		}
		if (tmpFlow === 'examples')
		{
			const tmpFound = this._findForm(tmpID);
			if (tmpFound && !this._isDelivered(tmpFound.Form))
			{
				return fBlock('This form has not been delivered yet. Build & deliver it first.');
			}
		}
		if (tmpFlow === 'build')
		{
			if (!String(this._deliverTo || '').trim() && !this._validateOnly)
			{
				return fBlock('Say where to deliver it, or tick Validate only.');
			}
			if (this._stage.composer.length)
			{
				return { Disabled: false, NoteHTML: 'Staged files are not used by Build & deliver; they stay staged for the other flows.' };
			}
			return { Disabled: false, NoteHTML: '' };
		}
		const tmpBusyFiles = this._stage.composer.filter((pFile) => (pFile.Status === 'uploading') || (pFile.Status === 'converting')).length;
		if (tmpBusyFiles)
		{
			return fBlock(`Waiting for ${tmpBusyFiles} file${tmpBusyFiles === 1 ? '' : 's'} to finish uploading.`);
		}
		if (this._stage.composer.some((pFile) => pFile.Status === 'error'))
		{
			return fBlock('Remove or retry the file that failed.');
		}
		return { Disabled: false, NoteHTML: '' };
	}

	/** @return {string} the Recent sessions list */
	_sessionsHTML()
	{
		const tmpList = Array.isArray(this._sessions) ? this._sessions : [];
		const tmpHead = `<div class="fcg-sess-h"><h2 class="fcg-h2">Recent sessions</h2>${tmpList.length ? `<span class="fcg-muted">${tmpList.length}</span>` : ''}</div>`;
		if (this._sessionsLoading && !tmpList.length)
		{
			return `${tmpHead}<div class="fcg-note"><i class="fas fa-circle-notch fa-spin"></i> Loading sessions…</div>`;
		}
		if (this._sessionsError && !tmpList.length)
		{
			return `${tmpHead}<div class="fcg-note fcg-note-error"><i class="fas fa-circle-exclamation"></i> ${this._esc(this._sessionsError)}</div>`;
		}
		if (!tmpList.length)
		{
			return `${tmpHead}<div class="fcg-note">No sessions yet. Your first Go starts one.</div>`;
		}
		const tmpShown = this._showAllSessions ? tmpList : tmpList.slice(0, SESSION_LIST_SHOW);
		const tmpRows = tmpShown.map((pRow) =>
		{
			const tmpID = Number(pRow && pRow.IDRequest) || 0;
			if (!tmpID)
			{
				return '';
			}
			const tmpFlow = String(pRow.Flow || '').toLowerCase();
			const tmpStatus = SESSION_STATUS_META[pRow.Status] || { Chip: pRow.Status || '', Cls: 'fcg-st-off' };
			return `<a class="fcg-srow" href="#/FormConcierge/Session/${tmpID}">`
				+ `<span class="fcg-srow-main"><span class="fcg-srow-name">${this._esc(pRow.FormName || pRow.Name || `Request ${tmpID}`)}</span>`
				+ `<span class="fcg-srow-req">${this._esc(pRow.Name || '')}</span></span>`
				+ `<span class="fcg-chip fcg-chip-flow">${this._esc(FLOW_CHIP[tmpFlow] || pRow.Flow || '')}</span>`
				+ `${tmpStatus.Chip ? `<span class="fcg-chip ${tmpStatus.Cls}">${this._esc(tmpStatus.Chip)}</span>` : ''}`
				+ `<span class="fcg-srow-when">${this._esc(this._relTime(pRow.CreateDate))}</span></a>`;
		}).join('');
		const tmpMore = (tmpList.length > SESSION_LIST_SHOW)
			? `<button type="button" class="fcg-linkbtn" onclick="_Pict.providers.FormConcierge.toggleAllSessions(); return false;">${this._showAllSessions ? 'Show fewer' : `Show all ${tmpList.length}`}</button>`
			: '';
		return `${tmpHead}<div class="fcg-slist">${tmpRows}</div>${tmpMore}`;
	}

	/** Repaint #FCG-Sessions. */
	_paintSessions() { this._assign('#FCG-Sessions', this._sessionsHTML()); }

	/** Show all / fewer sessions. */
	toggleAllSessions()
	{
		this._showAllSessions = !this._showAllSessions;
		this._paintSessions();
	}

	// ── Inputs ─────────────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * @param {any} pValue - the Form name input
	 */
	setFormName(pValue) { this._formName = String(pValue == null ? '' : pValue).slice(0, 200); }

	/**
	 * @param {any} pValue - the Deliver to input
	 */
	setDeliverTo(pValue)
	{
		this._deliverTo = String(pValue == null ? '' : pValue).slice(0, 200);
		this._paintGo();
	}

	/**
	 * @param {any} pChecked - Validate only (don't push)
	 */
	setValidateOnly(pChecked)
	{
		this._validateOnly = !!pChecked;
		const tmpInput = this._el('FCG-DeliverTo');
		if (tmpInput)
		{
			tmpInput.disabled = this._validateOnly;
		}
		this._paintGo();
	}

	/**
	 * Re-fetch Status (the explicit way to clear NodeBusy) and repaint what depends on it.
	 * @return {Promise<void>}
	 */
	async recheckNode()
	{
		await this._refreshStatus();
		this._paintHeader();
		if (this.state.Mode === 'home')
		{
			this._paintGo();
		}
		else
		{
			this._syncFollowUpLock();
		}
		this._toast(this._nodeBusy ? 'The plansheet node is still busy.' : 'The plansheet node is free.', 'info');
	}

	/**
	 * GET Status again (before Go/Send) to refresh NodeBusy. Errors leave the previous status in place.
	 * @return {Promise<any>} the NodeBusy record or null
	 */
	async _refreshStatus()
	{
		try
		{
			const tmpStatus = await this.api.getStatus();
			if (tmpStatus && !tmpStatus.Unreachable)
			{
				this._status = tmpStatus;
				this._nodeBusy = tmpStatus.NodeBusy || null;
			}
		}
		catch (pError)
		{
			// keep the last known status; the façade's own single-flight check (409 NodeBusy) still applies
		}
		return this._nodeBusy;
	}

	// ── Forms + picker ─────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * @param {number} pIDProject
	 * @return {{ Form: any, Indexed: boolean }|null}
	 */
	_findForm(pIDProject)
	{
		const tmpID = Number(pIDProject) || 0;
		const tmpForms = this._forms || { Forms: [], Unindexed: [] };
		const tmpIndexed = (tmpForms.Forms || []).find((pForm) => Number(pForm && pForm.IDProject) === tmpID);
		if (tmpIndexed)
		{
			return { Form: tmpIndexed, Indexed: true };
		}
		const tmpLoose = (tmpForms.Unindexed || []).find((pForm) => Number(pForm && pForm.IDProject) === tmpID);
		return tmpLoose ? { Form: tmpLoose, Indexed: false } : null;
	}

	/**
	 * @param {number} pIDProject
	 * @return {string} a display name (plain text; callers escape)
	 */
	_formLabel(pIDProject)
	{
		const tmpFound = this._findForm(pIDProject);
		return (tmpFound && tmpFound.Form.Name) ? String(tmpFound.Form.Name) : `Form #${Number(pIDProject) || 0}`;
	}

	/**
	 * @param {any} pForm
	 * @return {boolean} delivered (a Delivery record, or HeadlightForm.Pushed === true)
	 */
	_isDelivered(pForm)
	{
		return !!(pForm && ((pForm.Delivery && pForm.Delivery.Hash) || (pForm.HeadlightForm && (pForm.HeadlightForm.Pushed === true))));
	}

	/**
	 * Picker rows for the current flow: indexed forms first, then unindexed (examples → delivered only).
	 * @param {any} pTerm
	 * @return {Array<{ Value: number, Text: string, Group: string }>}
	 */
	_formRows(pTerm)
	{
		const tmpFlow = this.state.Flow;
		const tmpTerm = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
		const tmpForms = this._forms || { Forms: [], Unindexed: [] };
		// Straight quotes only: option text renders through a pict template.
		const fText = (pForm) => String(pForm.Name || `Form #${Number(pForm.IDProject) || 0}`).replace(/[‘’]/g, '\'').replace(/[“”]/g, '"');
		const fKeep = (pForm) => !!pForm && (Number(pForm.IDProject) > 0)
			&& ((tmpFlow !== 'examples') || this._isDelivered(pForm))
			&& (!tmpTerm || (fText(pForm).toLowerCase().indexOf(tmpTerm) >= 0) || (String(pForm.IDProject) === tmpTerm));
		/** @type {Array<{ Value: number, Text: string, Group: string }>} */
		const tmpRows = [];
		(tmpForms.Forms || []).filter(fKeep).forEach((pForm) => tmpRows.push({ Value: Number(pForm.IDProject), Text: fText(pForm), Group: GROUP_INDEXED }));
		(tmpForms.Unindexed || []).filter(fKeep).forEach((pForm) => tmpRows.push({ Value: Number(pForm.IDProject), Text: fText(pForm), Group: GROUP_UNINDEXED }));
		return tmpRows;
	}

	/** Mount the searchable form picker into #FCG-FormPicker (no-op when that span isn't painted). */
	_mountPicker()
	{
		if (!this._has('#FCG-FormPicker'))
		{
			return;
		}
		const tmpPicker = this.pickerProvider;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function'))
		{
			this._assign('#FCG-FormPicker', `<div class="fcg-muted">The form picker isn't available.</div>`);
			return;
		}
		const tmpInstance = tmpPicker.createPicker(PICKER_HASH,
			{
				DestinationAddress: '#FCG-FormPicker',
				ValueAddress: 'AppData.FormConcierge.PickedForm',
				Searchable: true,
				AllowClear: false,
				Placeholder: 'Search forms',
				EmptyLabel: 'No matching forms',
				DataProvider: (pTerm) =>
				{
					const tmpRows = this._formRows(pTerm);
					return Promise.resolve({ results: tmpRows.slice(0, PICKER_PAGE), hasMore: tmpRows.length > PICKER_PAGE });
				},
				ResolveValue: (pValue) =>
				{
					const tmpID = this._validID(pValue);
					return Promise.resolve(tmpID ? { Value: tmpID, Text: this._formLabel(tmpID).replace(/[‘’]/g, '\'').replace(/[“”]/g, '"') } : null);
				},
				OnChange: (pValue) =>
				{
					this._onPickForm(pValue);
				},
			});
		if (tmpInstance && (typeof tmpInstance.render === 'function'))
		{
			tmpInstance.render();
		}
		this._setPickerValue(this.state.IDProject);
	}

	/**
	 * Set the picker's value in place (no paint).
	 * @param {number} pIDProject
	 */
	_setPickerValue(pIDProject)
	{
		const tmpView = this.pict.views && this.pict.views[PICKER_HASH];
		if (tmpView && this._has('#FCG-FormPicker') && (typeof tmpView.setValue === 'function'))
		{
			tmpView.setValue(Number(pIDProject) || '');
		}
	}

	/**
	 * A form was picked: adopt it first when it isn't in Concierge yet (confirm), then write the route.
	 * @param {any} pValue
	 * @return {Promise<void>}
	 */
	async _onPickForm(pValue)
	{
		const tmpFlow = this.state.Flow;
		const tmpID = this._validID(pValue);
		if (!tmpID)
		{
			this._navigate(`#/FormConcierge/${tmpFlow}`);
			return;
		}
		const tmpFound = this._findForm(tmpID);
		if (tmpFound && !tmpFound.Indexed)
		{
			const tmpName = String(tmpFound.Form.Name || `Form #${tmpID}`);
			const tmpEnvironment = this._cleanName(this._status && this._status.Environment, 60);
			const tmpWhere = tmpEnvironment ? `the ${tmpEnvironment} environment` : 'this environment';
			const tmpModal = this.modal;
			// The modal escapes the message; adopting claims the plansheet project for this environment only.
			const tmpOK = (tmpModal && (typeof tmpModal.confirm === 'function'))
				? await tmpModal.confirm(`Adopt "${tmpName}" into Form Concierge? It becomes one of this customer's Concierge forms in ${tmpWhere}, and no other environment can adopt it afterwards.`, { title: 'Adopt plansheet form', confirmLabel: 'Adopt', cancelLabel: 'Cancel' })
				: false;
			if (!tmpOK)
			{
				this._setPickerValue(this.state.IDProject);
				return;
			}
			try
			{
				await this.api.adoptForm(tmpID);
				await this._loadFormsAsync();
				this._toast('Adopted into Form Concierge.', 'success');
			}
			catch (pError)
			{
				this._toast(this._errorText(pError), 'error');
				this._setPickerValue(this.state.IDProject);
				return;
			}
		}
		this._navigate(`#/FormConcierge/${tmpFlow}/${tmpID}`);
	}

	// ── Markdown editors (D6) ──────────────────────────────────────────────────────────────────────────────────

	/** @return {Array<any>} the default top-right buttons minus the image picker */
	_editorButtonsTR()
	{
		const tmpDefaults = /** @type {any} */ (libMarkdownEditor).default_configuration;
		const tmpButtons = (tmpDefaults && Array.isArray(tmpDefaults.ButtonsTR)) ? tmpDefaults.ButtonsTR : [];
		return tmpButtons.filter((pButton) => pButton && (pButton.Action !== 'openImagePicker'));
	}

	/**
	 * Mount (or re-render) a markdown box, seeded only with the user's own draft (never upstream text).
	 * @param {string} pContext - composer | followup
	 */
	_mountEditor(pContext)
	{
		const tmpEditor = EDITORS[pContext];
		if (!tmpEditor || !this._has(tmpEditor.Mount))
		{
			return;
		}
		this.state[tmpEditor.Key] = [ { Content: String(this[tmpEditor.Draft] || '') } ];
		if (!this.pict.views[tmpEditor.ViewID])
		{
			this.pict.addView(tmpEditor.ViewID,
				{
					ViewIdentifier: tmpEditor.ViewID,
					TargetElementAddress: tmpEditor.Mount,
					ContentDataAddress: tmpEditor.Address,
					AutoRender: false,
					EnableRichPreview: false,
					ReadOnly: false,
					ButtonsTL: [],
					ButtonsBL: [],
					ButtonsTR: this._editorButtonsTR(),
					FCGContext: pContext,
				}, FormConciergeMarkdownEditor);
		}
		const tmpView = this.pict.views[tmpEditor.ViewID];
		if (!tmpView)
		{
			return;
		}
		// The image preview builds <img src> for every ![](url) in the text, which would fire same-origin GETs.
		tmpView._updateImagePreviews = function () {};
		try
		{
			tmpView.render();
		}
		catch (pError)
		{
			// the box falls back to an empty mount if the CodeMirror bundle hiccups
		}
	}

	/**
	 * Pull a box's text back into its draft (before any repaint of an ancestor, a flow switch, or a submit).
	 * Guarded by DOM presence so a persisted editor never surfaces stale text.
	 * @param {string} pContext
	 */
	_captureEditor(pContext)
	{
		const tmpEditor = EDITORS[pContext];
		if (!tmpEditor || !this._has(tmpEditor.Mount))
		{
			return;
		}
		const tmpView = this.pict.views && this.pict.views[tmpEditor.ViewID];
		if (tmpView && (typeof tmpView.marshalFromView === 'function'))
		{
			try
			{
				tmpView.marshalFromView();
			}
			catch (pError)
			{
				// editor already gone
			}
		}
		const tmpSegments = Array.isArray(this.state[tmpEditor.Key]) ? this.state[tmpEditor.Key] : [];
		this[tmpEditor.Draft] = tmpSegments.map((pSegment) => (pSegment && pSegment.Content) || '').join('\n\n');
	}

	/**
	 * Take both drafts from the live segment arrays (the editor writes them on every keystroke), whether or not the
	 * box is still mounted. A successful Go / Send and a session switch reset the arrays, so nothing submitted or
	 * belonging to another session comes back.
	 */
	_refreshDrafts()
	{
		Object.keys(EDITORS).forEach((pContext) =>
		{
			const tmpEditor = EDITORS[pContext];
			if (this._has(tmpEditor.Mount))
			{
				this._captureEditor(pContext);
				return;
			}
			const tmpSegments = this.state[tmpEditor.Key];
			if (Array.isArray(tmpSegments))
			{
				this[tmpEditor.Draft] = tmpSegments.map((pSegment) => (pSegment && pSegment.Content) || '').join('\n\n');
			}
		});
	}

	/**
	 * Empty a box after its text was submitted: the draft, the segment array, and the editor when it is mounted.
	 * @param {string} pContext - composer | followup
	 */
	_resetEditor(pContext)
	{
		const tmpEditor = EDITORS[pContext];
		if (!tmpEditor)
		{
			return;
		}
		this[tmpEditor.Draft] = '';
		this.state[tmpEditor.Key] = [ { Content: '' } ];
		this._mountEditor(pContext);
	}

	// ── Drop zone + staged files (D5) ──────────────────────────────────────────────────────────────────────────

	/**
	 * @param {string} pContext
	 * @return {string} the drop zone's inner markup
	 */
	_dropInnerHTML(pContext)
	{
		const tmpLiteral = (pContext === 'followup') ? 'followup' : 'composer';
		return `<i class="fas fa-cloud-arrow-up fcg-drop-ic"></i> <span>Drop files here or </span>`
			+ `<button type="button" class="fcg-linkbtn" onclick="_Pict.providers.FormConcierge.chooseFiles('${tmpLiteral}'); return false;">browse</button>`
			+ `<div class="fcg-drop-sub">Specs, spreadsheets, Word, PDF, sample data. Each file uploads as soon as you add it.</div>`;
	}

	/**
	 * @param {string} pContext
	 * @return {string} the escaped staged-file list
	 */
	_filesHTML(pContext)
	{
		const tmpList = this._stage[pContext] || [];
		if (!tmpList.length)
		{
			return '';
		}
		return `<ul class="fcg-filelist">${tmpList.map((pFile) => this._fileRowHTML(pFile)).join('')}</ul>`;
	}

	/**
	 * One staged file (name, size, upload state, conversion chip, remove/retry). The name is untrusted: escaped.
	 * @param {any} pFile
	 * @return {string}
	 */
	_fileRowHTML(pFile)
	{
		const tmpKey = Number(pFile.Key) || 0;
		const tmpPct = Math.round(Math.max(0, Math.min(1, Number(pFile.Progress) || 0)) * 100);
		let tmpState = '';
		if (pFile.UploadStatus === 'error')
		{
			tmpState = `<span class="fcg-chip fcg-st-err">Failed</span>`;
		}
		else if (pFile.UploadStatus === 'queued')
		{
			tmpState = `<span class="fcg-chip">Queued</span>`;
		}
		else if (pFile.UploadStatus === 'uploading')
		{
			tmpState = `<span class="fcg-chip fcg-st-run" id="FCG-FilePct-${tmpKey}">Uploading ${tmpPct}%</span>`;
		}
		else
		{
			tmpState = `<span class="fcg-chip fcg-st-ok">Uploaded</span>`;
		}
		const tmpBar = (pFile.UploadStatus === 'uploading')
			? `<div class="fcg-bar"><div class="fcg-bar-fill" id="FCG-FileBar-${tmpKey}" style="width:${tmpPct}%"></div></div>`
			: '';
		const tmpActions = [];
		if ((pFile.UploadStatus === 'error') && !pFile.NoRetry)
		{
			tmpActions.push(`<button type="button" class="fcg-linkbtn" onclick="_Pict.providers.FormConcierge.retryFile(${tmpKey}); return false;">Retry</button>`);
		}
		tmpActions.push(`<button type="button" class="fcg-iconbtn" title="Remove" onclick="_Pict.providers.FormConcierge.removeFile(${tmpKey}); return false;"><i class="fas fa-xmark"></i></button>`);
		const tmpError = pFile.Error ? `<div class="fcg-file-err">${this._esc(pFile.Error)}</div>` : '';
		return `<li class="fcg-file fcg-file-${this._esc(pFile.Status)}" id="FCG-File-${tmpKey}">`
			+ `<div class="fcg-file-row"><i class="fas ${pFile.TextNative ? 'fa-file-lines' : 'fa-file'}"></i>`
			+ `<span class="fcg-file-name" title="${this._esc(pFile.Name)}">${this._esc(pFile.Name)}</span>`
			+ `<span class="fcg-file-size">${this._esc(this._fmtSize(pFile.Size))}</span>${tmpState}${this._convChipHTML(pFile)}`
			+ `<span class="fcg-file-actions">${tmpActions.join('')}</span></div>${tmpBar}${tmpError}</li>`;
	}

	/**
	 * What the file will contribute to the form's corpus (C8), as an escaped chip.
	 * @param {any} pFile
	 * @return {string}
	 */
	_convChipHTML(pFile)
	{
		if (pFile.TextNative)
		{
			return `<span class="fcg-chip fcg-chip-conv" title="Linked as a text file source">text</span>`;
		}
		if (pFile.ConvertStatus === 'converting')
		{
			return `<span class="fcg-chip fcg-chip-conv"><i class="fas fa-circle-notch fa-spin"></i> extracting text</span>`;
		}
		const tmpNotes = (Array.isArray(pFile.Notes) && pFile.Notes.length) ? ` title="${this._esc(pFile.Notes.join(' · '))}"` : '';
		if (pFile.Markdown)
		{
			return `<span class="fcg-chip fcg-chip-conv fcg-st-ok"${tmpNotes}>to text · ${this._esc(Math.round(pFile.Markdown.length / 100) / 10)}k chars</span>`
				+ ((Array.isArray(pFile.Notes) && pFile.Notes.length) ? `<span class="fcg-file-note">${this._esc(pFile.Notes.join(' · '))}</span>` : '');
		}
		if ((Number(pFile.Size) || 0) <= REFERENCE_MAX_BYTES)
		{
			return `<span class="fcg-chip fcg-chip-conv fcg-st-warn"${tmpNotes}>no text · attached as a reference</span>`;
		}
		return `<span class="fcg-chip fcg-chip-conv fcg-st-err"${tmpNotes}>no text · over 5 MB, won't be linked</span>`;
	}

	/**
	 * Repaint one staging context's file list, then whatever depends on file state.
	 * @param {string} pContext
	 */
	_paintFiles(pContext)
	{
		this._assign(FILE_REGIONS[pContext] || FILE_REGIONS.composer, this._filesHTML(pContext));
		if (pContext === 'followup')
		{
			this._syncFollowUpLock();
		}
		else if (this.state.Mode === 'home')
		{
			this._paintGo();
		}
	}

	/**
	 * Per-file progress without repainting the list.
	 * @param {any} pFile
	 */
	_paintFileProgress(pFile)
	{
		const tmpKey = Number(pFile.Key) || 0;
		const tmpPct = Math.round(Math.max(0, Math.min(1, Number(pFile.Progress) || 0)) * 100);
		const tmpBar = this._el(`FCG-FileBar-${tmpKey}`);
		if (tmpBar && tmpBar.style)
		{
			tmpBar.style.width = `${tmpPct}%`;
		}
		const tmpLabel = this._el(`FCG-FilePct-${tmpKey}`);
		if (tmpLabel)
		{
			tmpLabel.textContent = `Uploading ${tmpPct}%`;
		}
	}

	/**
	 * Open the OS file picker (a hidden <input type=file multiple> appended to body).
	 * @param {string} pContext
	 */
	chooseFiles(pContext)
	{
		const tmpDoc = this._doc();
		if (!tmpDoc)
		{
			return;
		}
		let tmpInput = tmpDoc.getElementById('FCG-FileInput');
		if (!tmpInput)
		{
			tmpInput = tmpDoc.createElement('input');
			tmpInput.type = 'file';
			tmpInput.id = 'FCG-FileInput';
			tmpInput.multiple = true;
			tmpInput.style.display = 'none';
			tmpDoc.body.appendChild(tmpInput);
		}
		tmpInput.value = '';
		tmpInput.onchange = () =>
		{
			const tmpFiles = tmpInput.files;
			tmpInput.onchange = null;
			if (tmpFiles && tmpFiles.length)
			{
				this.addFiles(tmpFiles, pContext);
			}
		};
		tmpInput.click();
	}

	/**
	 * @param {any} pEvent
	 * @param {string} pContext
	 */
	onDragOver(pEvent, pContext)
	{
		if (pEvent && (typeof pEvent.preventDefault === 'function'))
		{
			pEvent.preventDefault();
			if (pEvent.dataTransfer)
			{
				pEvent.dataTransfer.dropEffect = 'copy';
			}
		}
		const tmpZone = this._el(pContext === 'followup' ? 'FCG-FU-Drop' : 'FCG-Drop');
		if (tmpZone && tmpZone.classList)
		{
			tmpZone.classList.add('fcg-dragover');
		}
	}

	/**
	 * @param {any} pEvent
	 * @param {string} pContext
	 */
	onDragLeave(pEvent, pContext)
	{
		const tmpZone = this._el(pContext === 'followup' ? 'FCG-FU-Drop' : 'FCG-Drop');
		if (tmpZone && tmpZone.classList)
		{
			tmpZone.classList.remove('fcg-dragover');
		}
	}

	/**
	 * A file dragged over the editor wrapper (outside CodeMirror) must not open in the browser.
	 * @param {any} pEvent
	 */
	onEditorDragOver(pEvent)
	{
		const tmpTypes = pEvent && pEvent.dataTransfer && pEvent.dataTransfer.types;
		if (tmpTypes && (Array.prototype.indexOf.call(tmpTypes, 'Files') >= 0) && (typeof pEvent.preventDefault === 'function'))
		{
			pEvent.preventDefault();
		}
	}

	/**
	 * Files dropped on a zone (or the editor wrapper) are staged and uploaded.
	 * @param {any} pEvent
	 * @param {string} pContext
	 */
	onDrop(pEvent, pContext)
	{
		this.onDragLeave(pEvent, pContext);
		const tmpFiles = pEvent && pEvent.dataTransfer && pEvent.dataTransfer.files;
		if (!tmpFiles || !tmpFiles.length)
		{
			return;
		}
		if (typeof pEvent.preventDefault === 'function')
		{
			pEvent.preventDefault();
		}
		if (typeof pEvent.stopPropagation === 'function')
		{
			pEvent.stopPropagation();
		}
		this.addFiles(tmpFiles, pContext);
	}

	/**
	 * @param {string} pName
	 * @param {string} pType
	 * @return {{ Kind: string, TextNative: boolean }}
	 */
	_classify(pName, pType)
	{
		const tmpFileText = this.deps.FileText;
		if (tmpFileText && (typeof tmpFileText.classifyFile === 'function'))
		{
			try
			{
				const tmpResult = tmpFileText.classifyFile(pName, pType);
				if (tmpResult)
				{
					return { Kind: String(tmpResult.Kind || 'binary'), TextNative: !!tmpResult.TextNative };
				}
			}
			catch (pError)
			{
				// local rule below
			}
		}
		const tmpTextNative = /\.(md|markdown|txt|csv|tsv|json)$/i.test(pName) || /^text\//i.test(pType) || /^application\/json$/i.test(pType);
		return { Kind: tmpTextNative ? 'text' : 'binary', TextNative: tmpTextNative };
	}

	/**
	 * Stage files and start their uploads (≤ 2 at a time) and conversions (binary only) right away.
	 * @param {any} pFiles - FileList or array of File
	 * @param {string} [pContext] - composer | followup
	 * @return {number} files added
	 */
	addFiles(pFiles, pContext)
	{
		const tmpContext = (pContext === 'followup') ? 'followup' : 'composer';
		const tmpFiles = Array.prototype.slice.call(pFiles || []);
		if (!tmpFiles.length)
		{
			return 0;
		}
		if ((tmpContext === 'composer') && (this.state.Flow === 'build'))
		{
			this._toast('Build & deliver does not take files. Add them from Create or Update.', 'info');
			return 0;
		}
		let tmpAdded = 0;
		tmpFiles.forEach((pFile) =>
		{
			if (!pFile)
			{
				return;
			}
			const tmpName = String(pFile.name || 'file');
			const tmpType = String(pFile.type || '');
			const tmpClass = this._classify(tmpName, tmpType);
			const tmpEntry =
			{
				Key: ++this._fileSeq, Context: tmpContext, Name: tmpName, Size: Number(pFile.size) || 0, Type: tmpType,
				Kind: tmpClass.Kind, TextNative: tmpClass.TextNative, Status: 'uploading', UploadStatus: 'queued',
				ConvertStatus: tmpClass.TextNative ? 'skipped' : 'converting', Progress: 0, IDMedia: 0, MimeType: tmpType,
				Markdown: null, Notes: [], Error: '', NoRetry: false, Removed: false,
				// IDProject → true once this entry's source is linked there (a retry never links it twice).
				LinkedTo: {},
			};
			Object.defineProperty(tmpEntry, '_file', { value: pFile, enumerable: false, writable: true });
			Object.defineProperty(tmpEntry, '_abort', { value: null, enumerable: false, writable: true });
			if (tmpEntry.Size > MAX_UPLOAD_BYTES)
			{
				tmpEntry.UploadStatus = 'error';
				tmpEntry.ConvertStatus = 'skipped';
				tmpEntry.Error = 'Too large to upload (the limit is 25 MB).';
				tmpEntry.NoRetry = true;
			}
			else if (!tmpClass.TextNative)
			{
				this._convert(tmpEntry);
			}
			this._syncFileStatus(tmpEntry);
			this._stage[tmpContext].push(tmpEntry);
			tmpAdded++;
		});
		this._paintFiles(tmpContext);
		this._pumpUploads();
		return tmpAdded;
	}

	/**
	 * Extract text from a binary file (never rejects; a failure just means no text twin).
	 * @param {any} pEntry
	 */
	_convert(pEntry)
	{
		const tmpFileText = this.deps.FileText;
		Promise.resolve()
			.then(() => tmpFileText.convertFileToMarkdown(pEntry._file))
			.then((pResult) =>
			{
				pEntry.Markdown = (pResult && (typeof pResult.Markdown === 'string') && pResult.Markdown) ? pResult.Markdown : null;
				pEntry.Notes = (pResult && Array.isArray(pResult.Notes)) ? pResult.Notes.map(String) : [];
			}, () =>
			{
				pEntry.Markdown = null;
				pEntry.Notes = [ 'Text extraction failed.' ];
			})
			.then(() =>
			{
				pEntry.ConvertStatus = 'done';
				if (!pEntry.Removed)
				{
					this._syncFileStatus(pEntry);
					this._paintFiles(pEntry.Context);
				}
			});
	}

	/**
	 * Status from the upload + conversion sub-states (D5: converting | uploading | ready | error).
	 * @param {any} pEntry
	 */
	_syncFileStatus(pEntry)
	{
		if (pEntry.UploadStatus === 'error')
		{
			pEntry.Status = 'error';
		}
		else if (pEntry.UploadStatus !== 'done')
		{
			pEntry.Status = 'uploading';
		}
		else if (pEntry.ConvertStatus === 'converting')
		{
			pEntry.Status = 'converting';
		}
		else
		{
			pEntry.Status = 'ready';
		}
	}

	/** Start queued uploads up to the concurrency limit (across both staging contexts). */
	_pumpUploads()
	{
		const tmpAll = this._stage.composer.concat(this._stage.followup);
		let tmpActive = tmpAll.filter((pEntry) => pEntry.UploadStatus === 'uploading').length;
		tmpAll.forEach((pEntry) =>
		{
			if ((tmpActive < MAX_CONCURRENT_UPLOADS) && (pEntry.UploadStatus === 'queued') && !pEntry.Removed)
			{
				tmpActive++;
				this._startUpload(pEntry);
			}
		});
	}

	/**
	 * Upload one original to the library anchor (XHR PUT with byte progress through the Api module).
	 * @param {any} pEntry
	 */
	_startUpload(pEntry)
	{
		pEntry.UploadStatus = 'uploading';
		pEntry.Progress = 0;
		pEntry.Error = '';
		this._syncFileStatus(pEntry);
		let tmpHandle = null;
		try
		{
			tmpHandle = this.api.uploadFile(pEntry._file, { onProgress: (pFraction) =>
			{
				pEntry.Progress = Number(pFraction) || 0;
				this._paintFileProgress(pEntry);
			} });
		}
		catch (pError)
		{
			tmpHandle = { promise: Promise.reject(pError), abort: null };
		}
		pEntry._abort = (tmpHandle && (typeof tmpHandle.abort === 'function')) ? tmpHandle.abort : null;
		Promise.resolve(tmpHandle && tmpHandle.promise)
			.then((pResult) =>
			{
				const tmpIDMedia = Number(pResult && pResult.IDMedia) || 0;
				if (!tmpIDMedia)
				{
					throw new Error('The upload did not return a media id.');
				}
				pEntry.IDMedia = tmpIDMedia;
				pEntry.MimeType = String((pResult && pResult.MimeType) || pEntry.Type || '');
				pEntry.UploadStatus = 'done';
				pEntry.Progress = 1;
			})
			.catch((pError) =>
			{
				pEntry.UploadStatus = 'error';
				pEntry.Error = this._errorText(pError);
			})
			.then(() =>
			{
				pEntry._abort = null;
				if (!pEntry.Removed)
				{
					this._syncFileStatus(pEntry);
					this._paintFiles(pEntry.Context);
				}
				this._pumpUploads();
			});
	}

	/**
	 * @param {number} pKey
	 * @return {any} the staged entry or null
	 */
	_findFile(pKey)
	{
		const tmpKey = Number(pKey) || 0;
		return this._stage.composer.concat(this._stage.followup).find((pEntry) => pEntry.Key === tmpKey) || null;
	}

	/**
	 * Drop a staged file (its Media stays in the library by design); aborts an upload in flight.
	 * @param {number} pKey
	 */
	removeFile(pKey)
	{
		const tmpEntry = this._findFile(pKey);
		if (!tmpEntry)
		{
			return;
		}
		tmpEntry.Removed = true;
		if (typeof tmpEntry._abort === 'function')
		{
			try
			{
				tmpEntry._abort();
			}
			catch (pError)
			{
				// already settled
			}
		}
		const tmpList = this._stage[tmpEntry.Context];
		const tmpIndex = tmpList.indexOf(tmpEntry);
		if (tmpIndex >= 0)
		{
			tmpList.splice(tmpIndex, 1);
		}
		this._paintFiles(tmpEntry.Context);
		this._pumpUploads();
	}

	/**
	 * Re-queue a failed upload.
	 * @param {number} pKey
	 */
	retryFile(pKey)
	{
		const tmpEntry = this._findFile(pKey);
		if (!tmpEntry || (tmpEntry.UploadStatus !== 'error') || tmpEntry.NoRetry)
		{
			return;
		}
		tmpEntry.UploadStatus = 'queued';
		tmpEntry.Error = '';
		this._syncFileStatus(tmpEntry);
		this._paintFiles(tmpEntry.Context);
		this._pumpUploads();
	}

	/**
	 * Forget a staging context (after a successful submit, or a session switch).
	 * @param {string} pContext
	 */
	_clearStage(pContext)
	{
		(this._stage[pContext] || []).forEach((pEntry) =>
		{
			pEntry.Removed = true;
			if (typeof pEntry._abort === 'function')
			{
				try
				{
					pEntry._abort();
				}
				catch (pError)
				{
					// already settled
				}
			}
		});
		this._stage[pContext] = [];
	}

	/**
	 * Take just the submitted entries out of a staging context; files added while the submit ran stay staged.
	 * @param {string} pContext
	 * @param {Array<any>} pEntries - the entries the submit used
	 */
	_unstage(pContext, pEntries)
	{
		const tmpSubmitted = pEntries || [];
		tmpSubmitted.forEach((pEntry) =>
		{
			pEntry.Removed = true;
		});
		this._stage[pContext] = (this._stage[pContext] || []).filter((pEntry) => tmpSubmitted.indexOf(pEntry) < 0);
	}

	/**
	 * The C8 corpus plan for staged files: text-native originals → one FILE source; binaries → their extracted text
	 * as a DATA source; a binary with no text → a Reference FILE when ≤ 5 MB, else skipped.
	 * @param {Array<any>} pFiles
	 * @param {string} pFlow
	 * @return {{ Sources: Array<any>, Entries: Array<any>, Skipped: Array<string>, Names: Array<string>, Extras: Array<any> }}
	 *   Entries[i] is the staged entry Sources[i] came from.
	 */
	_planSources(pFiles, pFlow)
	{
		const tmpRole = (pFlow === 'examples') ? 'Example' : 'Spec';
		/** @type {Array<any>} */
		const tmpSources = [];
		/** @type {Array<any>} */
		const tmpEntries = [];
		/** @type {Array<string>} */
		const tmpSkipped = [];
		/** @type {Array<any>} */
		const tmpExtras = [];
		(pFiles || []).forEach((pEntry) =>
		{
			if (!pEntry || (pEntry.Status !== 'ready') || !(Number(pEntry.IDMedia) > 0))
			{
				return;
			}
			const tmpName = this._cleanName(pEntry.Name, 200) || 'file';
			if (pEntry.TextNative)
			{
				tmpSources.push({ Kind: 'media', IDMedia: Number(pEntry.IDMedia), Name: tmpName, Role: tmpRole });
				tmpExtras.push({ Name: pEntry.Name, MimeType: pEntry.MimeType, Size: pEntry.Size, IDMedia: Number(pEntry.IDMedia), Converted: false, Role: tmpRole });
			}
			else if (pEntry.Markdown)
			{
				tmpSources.push({ Kind: 'text', Name: this._cleanName(`${pEntry.Name} (text)`, 200), Title: tmpName, Body: pEntry.Markdown, Role: tmpRole });
				tmpExtras.push({ Name: pEntry.Name, MimeType: pEntry.MimeType, Size: pEntry.Size, IDMedia: Number(pEntry.IDMedia), Converted: true, Role: tmpRole });
			}
			else if ((Number(pEntry.Size) || 0) <= REFERENCE_MAX_BYTES)
			{
				tmpSources.push({ Kind: 'media', IDMedia: Number(pEntry.IDMedia), Name: tmpName, Role: 'Reference' });
				tmpExtras.push({ Name: pEntry.Name, MimeType: pEntry.MimeType, Size: pEntry.Size, IDMedia: Number(pEntry.IDMedia), Converted: false, Role: 'Reference' });
			}
			else
			{
				tmpSkipped.push(pEntry.Name);
				return;
			}
			tmpEntries.push(pEntry);
		});
		return { Sources: tmpSources, Entries: tmpEntries, Skipped: tmpSkipped, Names: tmpSources.map((pSource) => pSource.Name), Extras: tmpExtras };
	}

	/**
	 * Link planned sources to a form so a retry never duplicates one, while a revised file is always linked.
	 *
	 * Dedupe is per staged entry: an entry links into a project once (entry.LinkedTo[IDProject]), so the retry of a
	 * Go / Send that failed after linking skips it. A form's older source with the same name is NOT a reason to
	 * skip: a revised Spec.docx arrives as another 'Spec.docx (text)' (text sources carry IDMedia 0), and dropping
	 * it would run the turn on the old text. The one server-side check left is D9's Name + IDMedia for media sources,
	 * where IDMedia is the exact upload (an earlier add whose answer was lost).
	 * @param {number} pIDProject
	 * @param {{ Sources: Array<any>, Entries: Array<any> }} pPlan - from _planSources
	 * @param {boolean} pFresh - the project was created by this same Go (nothing can be linked yet)
	 * @return {Promise<Array<any>>} the planned sources now on the form for this submit, in plan order: added by this
	 *   call or by an earlier failed attempt of the same submit (its entries are still staged, so the node has not
	 *   been told about them). NewSources is built from these.
	 */
	async _linkSources(pIDProject, pPlan, pFresh)
	{
		const tmpSources = (pPlan && Array.isArray(pPlan.Sources)) ? pPlan.Sources : [];
		const tmpEntries = (pPlan && Array.isArray(pPlan.Entries)) ? pPlan.Entries : [];
		const tmpKey = String(Number(pIDProject) || 0);
		/** @param {number} pIndex @return {boolean} */
		const fLinked = (pIndex) => !!(tmpEntries[pIndex] && tmpEntries[pIndex].LinkedTo && tmpEntries[pIndex].LinkedTo[tmpKey]);
		/** @param {number} pIndex */
		const fMark = (pIndex) =>
		{
			const tmpEntry = tmpEntries[pIndex];
			if (tmpEntry)
			{
				tmpEntry.LinkedTo = tmpEntry.LinkedTo || {};
				tmpEntry.LinkedTo[tmpKey] = true;
			}
		};
		let tmpPending = tmpSources.map((pSource, pIndex) => pIndex).filter((pIndex) => !fLinked(pIndex));
		if (!pFresh && tmpPending.some((pIndex) => Number(tmpSources[pIndex].IDMedia) > 0))
		{
			const tmpExisting = await this.api.getSources(pIDProject);
			/** @type {Record<string, boolean>} */
			const tmpMediaKeys = {};
			((tmpExisting && tmpExisting.Sources) || []).forEach((pRow) =>
			{
				const tmpIDMedia = Number(pRow && pRow.IDMedia) || 0;
				if (tmpIDMedia > 0)
				{
					tmpMediaKeys[`${String(pRow.Name)}|${tmpIDMedia}`] = true;
				}
			});
			tmpPending = tmpPending.filter((pIndex) =>
			{
				const tmpSource = tmpSources[pIndex];
				const tmpIDMedia = Number(tmpSource.IDMedia) || 0;
				if ((tmpIDMedia > 0) && tmpMediaKeys[`${tmpSource.Name}|${tmpIDMedia}`])
				{
					fMark(pIndex);
					return false;
				}
				return true;
			});
		}
		for (let i = 0; i < tmpPending.length; i += MAX_SOURCES_PER_CALL)
		{
			const tmpChunk = tmpPending.slice(i, i + MAX_SOURCES_PER_CALL);
			await this.api.addSources(pIDProject, tmpChunk.map((pIndex) => tmpSources[pIndex]));
			tmpChunk.forEach(fMark);
		}
		return tmpSources.filter((pSource, pIndex) => fLinked(pIndex));
	}

	// ── Go (D9 + C2–C5, C8) ────────────────────────────────────────────────────────────────────────────────────

	/**
	 * The default form name: the name input, else the request's first heading/line (≤ 80), else the first file's name,
	 * else "Untitled form <YYYY-MM-DD HH:mm>".
	 * @return {string}
	 */
	_defaultFormName()
	{
		const tmpTyped = String(this._formName || '').replace(/[\x00-\x1f\x7f]/g, '').trim();
		if (tmpTyped)
		{
			return tmpTyped.slice(0, 200);
		}
		const tmpText = String(this._draft || '');
		const tmpHeading = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/m.exec(tmpText);
		let tmpLine = tmpHeading ? tmpHeading[1] : (tmpText.split(/\r?\n/).map((pLine) => pLine.trim()).find((pLine) => !!pLine) || '');
		tmpLine = tmpLine.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1').replace(/^[-*+>\s]+/, '').replace(/[*_`#]/g, '').replace(/[\x00-\x1f\x7f]/g, '').trim();
		if (tmpLine)
		{
			return tmpLine.slice(0, 80).trim();
		}
		const tmpFirst = this._stage.composer.find((pEntry) => pEntry && pEntry.Name);
		if (tmpFirst)
		{
			const tmpBase = String(tmpFirst.Name).replace(/\.[A-Za-z0-9]{1,8}$/, '').replace(/[\x00-\x1f\x7f]/g, '').trim();
			if (tmpBase)
			{
				return tmpBase.slice(0, 80);
			}
		}
		const tmpNow = new Date(this._now());
		const fPad = (pN) => ((pN < 10) ? '0' : '') + pN;
		return `Untitled form ${tmpNow.getFullYear()}-${fPad(tmpNow.getMonth() + 1)}-${fPad(tmpNow.getDate())} ${fPad(tmpNow.getHours())}:${fPad(tmpNow.getMinutes())}`;
	}

	/**
	 * Busy text on #FCG-Go (the only region Busy repaints).
	 * @param {string} pText
	 */
	_setBusy(pText)
	{
		this.state.Busy = true;
		this.state.BusyText = pText;
		this._paintGo();
	}

	/** Clear Busy and repaint Go. */
	_clearBusy()
	{
		this.state.Busy = false;
		this.state.BusyText = '';
		this._paintGo();
	}

	/**
	 * Go: Status (NodeBusy) → workspace → form (create; kept for retries) → sources (deduped) → session → open it.
	 * Idempotent after a failure: the created form id is kept (and in the URL), and linked sources are skipped.
	 * @return {Promise<void>}
	 */
	async go()
	{
		const tmpState = this.state;
		if (tmpState.Busy || (tmpState.Mode !== 'home'))
		{
			return;
		}
		this._captureEditor('composer');
		const tmpFlow = tmpState.Flow;
		const tmpText = String(this._draft || '').trim();
		const tmpBlock = this._goBlock();
		if (tmpBlock.Disabled)
		{
			this._paintGo();
			return;
		}
		const tmpReady = (tmpFlow === 'build') ? [] : this._stage.composer.filter((pEntry) => pEntry.Status === 'ready');
		if ((tmpFlow === 'create') && !tmpText && !tmpReady.length)
		{
			this._toast('Describe the form, or add at least one file.', 'info');
			return;
		}
		if ((tmpFlow === 'update') && !tmpText)
		{
			this._toast('Describe the change you want.', 'info');
			return;
		}
		const tmpToken = this._routeToken();
		this._setBusy('Checking the plansheet node…');
		try
		{
			await this._refreshStatus();
			if (this._nodeBusy)
			{
				this._clearBusy();
				this._paintHeader();
				return;
			}
			if (!this._workspaceReady)
			{
				this._setBusy('Preparing the workspace…');
				await this.api.ensureWorkspace();
				this._workspaceReady = true;
			}
			let tmpIDProject = Number(tmpState.IDProject) || 0;
			let tmpFresh = false;
			if ((tmpFlow === 'create') && !tmpIDProject)
			{
				this._setBusy('Creating the form project…');
				const tmpCreated = await this.api.createForm({ Name: this._defaultFormName() });
				const tmpForm = (tmpCreated && tmpCreated.Form) || {};
				tmpIDProject = this._validID(tmpForm.IDProject);
				if (!tmpIDProject)
				{
					throw new Error('Plansheet did not return the new form project.');
				}
				tmpFresh = true;
				tmpState.IDProject = tmpIDProject;
				if (this._forms && Array.isArray(this._forms.Forms) && !this._findForm(tmpIDProject))
				{
					this._forms.Forms.unshift(tmpForm);
				}
				// Only while the user is still on this Go's route: never rewrite the URL of a screen they moved to.
				if (this._routeToken() === tmpToken)
				{
					this._replaceHash(`#/FormConcierge/create/${tmpIDProject}`);
					// The screen now matches the URL it wrote, so a Retry after a failed reload resumes this form.
					this._lastApply = (pProvider) => pProvider.routeApplyFlow('create', tmpIDProject);
				}
				this._paintTarget();
			}
			const tmpPlan = this._planSources(tmpReady, tmpFlow);
			/** @type {Array<any>} */
			let tmpLinked = [];
			if (tmpPlan.Sources.length)
			{
				this._setBusy(`Linking ${tmpPlan.Sources.length} source${tmpPlan.Sources.length === 1 ? '' : 's'} to the form…`);
				tmpLinked = await this._linkSources(tmpIDProject, tmpPlan, tmpFresh);
			}
			if (tmpPlan.Skipped.length)
			{
				this._toast(`Not linked (no text could be extracted and over 5 MB): ${tmpPlan.Skipped.join(', ')}`, 'warning');
			}
			this._setBusy('Starting the run…');
			/** @type {any} */
			const tmpBody = { Flow: tmpFlow, IDProject: tmpIDProject, Text: tmpText };
			if (tmpFlow === 'build')
			{
				if (this._validateOnly)
				{
					tmpBody.ValidateOnly = true;
				}
				else
				{
					tmpBody.DeliverTo = String(this._deliverTo || '').trim().slice(0, 200);
				}
			}
			// Announce only what is on the form for this turn, never a planned name that did not get linked.
			if ((tmpFlow === 'examples') && tmpLinked.length)
			{
				tmpBody.NewSources = tmpLinked.map((pSource) => pSource.Name);
			}
			let tmpResult = null;
			try
			{
				tmpResult = await this.api.startSession(tmpBody);
			}
			catch (pError)
			{
				if ((tmpFlow !== 'build') || !pError || (pError.Code !== 'NoBlueprint'))
				{
					throw pError;
				}
				const tmpModal = this.modal;
				const tmpForce = (tmpModal && (typeof tmpModal.confirm === 'function'))
					? await tmpModal.confirm('This form has no agreed blueprint yet. Build it anyway from its sources?', { title: 'No blueprint', confirmLabel: 'Build anyway', cancelLabel: 'Cancel' })
					: false;
				if (!tmpForce)
				{
					this._clearBusy();
					return;
				}
				tmpBody.Force = true;
				tmpResult = await this.api.startSession(tmpBody);
			}
			const tmpIDRequest = this._validID(tmpResult && tmpResult.IDRequest);
			if (!tmpIDRequest)
			{
				throw new Error('Plansheet did not return a session id.');
			}
			if (tmpResult && Array.isArray(tmpResult.Warnings) && (tmpResult.Warnings.indexOf('DeliveryIdentityUnrecognized') >= 0))
			{
				this._toast('The recorded delivery identity was not recognized, so the in-place guardrail was left out. Check the reply before pushing.', 'warning');
			}
			if (tmpPlan.Extras.length)
			{
				this._sessionFiles[tmpIDRequest] = (this._sessionFiles[tmpIDRequest] || []).concat(tmpPlan.Extras);
			}
			// Success (D7): clear the draft — the segment array and the mounted box too, or the route to the session
			// would capture the submitted text back into the draft — and take out only the files this Go submitted
			// (Build used none; files dropped while it ran stay staged). Then reset Create.
			this._resetEditor('composer');
			this._unstage('composer', tmpReady);
			if (tmpFlow === 'create')
			{
				this._formName = '';
				tmpState.IDProject = 0;
			}
			if (tmpFlow === 'build')
			{
				this._deliverTo = '';
				this._validateOnly = false;
			}
			tmpState.Busy = false;
			tmpState.BusyText = '';
			if (this._routeToken() === tmpToken)
			{
				this._paintFiles('composer');
				if (tmpFlow === 'create')
				{
					// The started form must not stay in history as "finish starting it": Back from the session lands Home.
					this._replaceHash('#/FormConcierge');
				}
				this._navigate(`#/FormConcierge/Session/${tmpIDRequest}`);
			}
			else
			{
				this._toast(`Session #${tmpIDRequest} started. It is in Recent sessions.`, 'success');
				if (this.state.Mode === 'home')
				{
					this._paintFiles('composer');
				}
			}
		}
		catch (pError)
		{
			this._onSubmitError(pError);
			this._clearBusy();
		}
	}

	/**
	 * A Go/Send/resubmit failure: NodeBusy disables the buttons (until Check again); everything else toasts.
	 * @param {any} pError
	 */
	_onSubmitError(pError)
	{
		if (pError && (pError.Code === 'NodeBusy'))
		{
			const tmpBody = pError.Body || {};
			this._nodeBusy = { IDRequest: Number(tmpBody.IDRequest) || 0, StageLabel: '' };
			this._paintHeader();
		}
		this._toast(this._errorText(pError), (pError && (pError.Code === 'NodeBusy')) ? 'warning' : 'error');
	}

	// ── Follow-up, cancel, resubmit ────────────────────────────────────────────────────────────────────────────

	/** @return {string} why Send is off ('' = on) */
	_sendBlockReason()
	{
		const tmpPayload = this._session;
		if (!tmpPayload)
		{
			return 'Loading the session…';
		}
		if (this._phase && this._phase.Active)
		{
			return 'A run is in progress. Keep typing; Send unlocks when it finishes.';
		}
		const tmpIDRequest = Number(tmpPayload.Session && tmpPayload.Session.IDRequest) || 0;
		if (this._nodeBusy && (Number(this._nodeBusy.IDRequest) !== tmpIDRequest))
		{
			return `The plansheet node is busy${Number(this._nodeBusy.IDRequest) ? ` with request #${Number(this._nodeBusy.IDRequest)}` : ''}. Send stays off until it finishes.`;
		}
		const tmpBusy = this._stage.followup.filter((pEntry) => (pEntry.Status === 'uploading') || (pEntry.Status === 'converting')).length;
		if (tmpBusy)
		{
			return `Waiting for ${tmpBusy} file${tmpBusy === 1 ? '' : 's'} to finish uploading.`;
		}
		if (this._stage.followup.some((pEntry) => pEntry.Status === 'error'))
		{
			return 'Remove or retry the file that failed.';
		}
		return '';
	}

	/** Toggle Send (disabled + note) and the follow-up wrapper class — never a repaint. */
	_syncFollowUpLock()
	{
		const tmpReason = this._sendBlockReason();
		const tmpButton = this._el('FCG-SendBtn');
		if (tmpButton)
		{
			tmpButton.disabled = !!tmpReason || !!this.state.SendBusy;
			tmpButton.innerHTML = this.state.SendBusy ? '<i class="fas fa-circle-notch fa-spin"></i> Sending…' : '<i class="fas fa-paper-plane"></i> Send';
		}
		const tmpWrap = this._el('FCG-FollowUp');
		if (tmpWrap && tmpWrap.classList)
		{
			tmpWrap.classList.toggle('fcg-fu-locked', !!(this._phase && this._phase.Active));
		}
		const tmpNote = this._el('FCG-SendNote');
		if (tmpNote)
		{
			tmpNote.textContent = tmpReason;
		}
		const tmpRecheck = this._el('FCG-RecheckBtn');
		if (tmpRecheck && tmpRecheck.classList)
		{
			const tmpIDRequest = Number(this._session && this._session.Session && this._session.Session.IDRequest) || 0;
			tmpRecheck.classList.toggle('fcg-hidden', !(this._nodeBusy && (Number(this._nodeBusy.IDRequest) !== tmpIDRequest)));
		}
	}

	/**
	 * Send a follow-up: Status (NodeBusy) → new files become sources first (C7) → POST Message → poll.
	 * @return {Promise<void>}
	 */
	async sendFollowUp()
	{
		const tmpState = this.state;
		if (tmpState.SendBusy || (tmpState.Mode !== 'session') || !this._session)
		{
			return;
		}
		this._captureEditor('followup');
		const tmpText = String(this._followDraft || '').trim();
		const tmpReason = this._sendBlockReason();
		if (tmpReason)
		{
			this._toast(tmpReason, 'info');
			this._syncFollowUpLock();
			return;
		}
		if (!tmpText)
		{
			this._toast('Write the follow-up first.', 'info');
			return;
		}
		const tmpPayload = this._session;
		const tmpIDRequest = Number(tmpState.IDRequest) || 0;
		const tmpIDProject = Number((tmpPayload.Form && tmpPayload.Form.IDProject) || (tmpPayload.Session && tmpPayload.Session.IDProject)) || 0;
		const tmpFlow = String((tmpPayload.Session && tmpPayload.Session.Flow) || '').toLowerCase();
		tmpState.SendBusy = true;
		this._syncFollowUpLock();
		try
		{
			await this._refreshStatus();
			if (this._nodeBusy && (Number(this._nodeBusy.IDRequest) !== tmpIDRequest))
			{
				tmpState.SendBusy = false;
				this._syncFollowUpLock();
				this._toast(this._sendBlockReason() || ERROR_TEXT.NodeBusy, 'warning');
				return;
			}
			const tmpReady = this._stage.followup.filter((pEntry) => pEntry.Status === 'ready');
			const tmpPlan = this._planSources(tmpReady, tmpFlow === 'examples' ? 'examples' : 'other');
			/** @type {Array<any>} */
			let tmpLinked = [];
			if (tmpPlan.Sources.length && tmpIDProject)
			{
				tmpLinked = await this._linkSources(tmpIDProject, tmpPlan, false);
			}
			if (tmpPlan.Skipped.length)
			{
				this._toast(`Not linked (no text could be extracted and over 5 MB): ${tmpPlan.Skipped.join(', ')}`, 'warning');
			}
			/** @type {any} */
			const tmpBody = { Text: tmpText };
			if (tmpLinked.length)
			{
				tmpBody.NewSources = tmpLinked.map((pSource) => pSource.Name);
			}
			await this.api.sendMessage(tmpIDRequest, tmpBody);
			if (tmpPlan.Extras.length)
			{
				this._sessionFiles[tmpIDRequest] = (this._sessionFiles[tmpIDRequest] || []).concat(tmpPlan.Extras);
			}
			// Only the submitted files leave staging (files dropped during the send stay). The box is emptied only
			// while it still belongs to this session: a switch mid-send gave it, and its staging, to another one.
			this._unstage('followup', tmpReady);
			tmpState.SendBusy = false;
			if (this._followFor === tmpIDRequest)
			{
				this._resetEditor('followup');
			}
			this._paintFiles('followup');
			if (Number(tmpState.IDRequest) === tmpIDRequest)
			{
				await this._openSession(true);
			}
		}
		catch (pError)
		{
			tmpState.SendBusy = false;
			this._onSubmitError(pError);
			this._syncFollowUpLock();
		}
	}

	/**
	 * Cancel the active run (best-effort upstream; a claimed run may still finish), then refresh.
	 * @return {Promise<void>}
	 */
	async cancelRun()
	{
		const tmpIDRequest = Number(this.state.IDRequest) || 0;
		if (!tmpIDRequest || (this.state.Mode !== 'session'))
		{
			return;
		}
		const tmpModal = this.modal;
		const tmpOK = (tmpModal && (typeof tmpModal.confirm === 'function'))
			? await tmpModal.confirm('Ask the plansheet node to cancel this run? A run that has already started may still finish.', { title: 'Cancel run', confirmLabel: 'Cancel the run', cancelLabel: 'Keep it running', dangerous: true })
			: false;
		if (!tmpOK)
		{
			return;
		}
		try
		{
			const tmpResult = await this.api.cancelSession(tmpIDRequest);
			this._toast((tmpResult && tmpResult.Claimed)
				? 'Cancel requested. The node stops at its next heartbeat and may still finish.'
				: 'Cancelled. The run had not started.', 'info');
		}
		catch (pError)
		{
			this._toast(this._errorText(pError), 'error');
		}
		if ((this.state.Mode === 'session') && (Number(this.state.IDRequest) === tmpIDRequest))
		{
			await this._openSession(true);
		}
	}

	/**
	 * Submit again (not picked up / cancelled before starting) or Try again (failed), always from the STRIPPED user
	 * text, never raw Content: an examples turn re-runs the examples flow; a never-started first turn becomes a new
	 * session of the same flow (Build goes back to its composer for the delivery target); anything else is re-sent as
	 * a follow-up.
	 * @return {Promise<void>}
	 */
	async resubmit()
	{
		const tmpState = this.state;
		const tmpPayload = this._session;
		const tmpPhase = this._phase;
		if (!tmpPayload || !tmpPhase || !tmpPhase.Terminal || tmpState.SendBusy || (tmpState.Mode !== 'session'))
		{
			return;
		}
		const tmpMessages = this._messages(tmpPayload);
		const tmpUserTurns = tmpMessages.filter((pMessage) => !this._isAssistant(pMessage));
		const tmpLastUser = tmpUserTurns[tmpUserTurns.length - 1] || null;
		const tmpText = String(this._userText(tmpLastUser)).trim();
		const tmpSession = tmpPayload.Session || {};
		const tmpFlow = String(tmpSession.Flow || '').toLowerCase();
		const tmpIDProject = Number((tmpPayload.Form && tmpPayload.Form.IDProject) || tmpSession.IDProject) || 0;
		const tmpIDRequest = Number(tmpSession.IDRequest || tmpState.IDRequest) || 0;
		const tmpExamplesPrefix = this.md.EXAMPLES_PREFIX || DEFAULT_EXAMPLES_PREFIX;
		const tmpIsExamples = !!tmpLastUser && (String(tmpLastUser.Content || '').indexOf(tmpExamplesPrefix) === 0);
		const tmpNewSession = tmpPhase.SubmitAgain && (tmpUserTurns.length <= 1);
		if (tmpNewSession && ([ 'build', 'config' ].indexOf(tmpFlow) >= 0) && !tmpIsExamples)
		{
			this._toast('Enter the delivery target again, then Build & deliver.', 'info');
			this._navigate(`#/FormConcierge/build/${tmpIDProject}`);
			return;
		}
		if (!tmpText && !(tmpNewSession || tmpIsExamples))
		{
			this._toast('There is no request text to send again.', 'info');
			return;
		}
		tmpState.SendBusy = true;
		this._syncFollowUpLock();
		try
		{
			await this._refreshStatus();
			if (this._nodeBusy && (Number(this._nodeBusy.IDRequest) !== tmpIDRequest))
			{
				tmpState.SendBusy = false;
				this._syncFollowUpLock();
				this._toast(this._sendBlockReason() || ERROR_TEXT.NodeBusy, 'warning');
				return;
			}
			if (tmpIsExamples || tmpNewSession)
			{
				const tmpNewFlow = tmpIsExamples ? 'examples' : (([ 'update', 'modify' ].indexOf(tmpFlow) >= 0) ? 'update' : 'create');
				const tmpResult = await this.api.startSession({ Flow: tmpNewFlow, IDProject: tmpIDProject, Text: tmpText });
				const tmpNewID = this._validID(tmpResult && tmpResult.IDRequest);
				tmpState.SendBusy = false;
				if (!tmpNewID)
				{
					throw new Error('Plansheet did not return a session id.');
				}
				if (tmpNewID === Number(tmpState.IDRequest))
				{
					await this._openSession(true);
				}
				else
				{
					this._navigate(`#/FormConcierge/Session/${tmpNewID}`);
				}
				return;
			}
			await this.api.sendMessage(tmpIDRequest, { Text: tmpText });
			tmpState.SendBusy = false;
			await this._openSession(true);
		}
		catch (pError)
		{
			tmpState.SendBusy = false;
			this._onSubmitError(pError);
			this._syncFollowUpLock();
		}
	}
}

module.exports = FormConciergeProvider;
module.exports.default_configuration = { ProviderIdentifier: 'FormConcierge' };
module.exports.FormConciergeMarkdownEditor = FormConciergeMarkdownEditor;
